import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  getVoiceStatus,
  interruptVoiceSession,
  recallMemory,
  sendVoiceCommand,
  speakVoiceText,
  startVoiceConversation,
  syncVoiceState,
  transcribeVoiceAudio,
  updateMemoryPreferences,
} from "@/services/api";
import { toast } from "@/components/ui/sonner";
import {
  shouldDisableHandsFreeForStop,
  shouldPreserveHandsFree,
  shouldPreserveHandsFreeAfterRecognitionError,
} from "@/lib/voiceHandsFreePolicy";
import {
  getVoiceAudioFormatForMime,
  getVoiceBrowserFamily,
  getVoiceRecorderMimeCandidates,
  isCurrentIOSLikeBrowser,
  mergeSpeechRecognitionResults,
  classifySpeechRecognitionError,
  resolveVoiceCaptureStrategy,
  shouldStopContinuousRecognitionAfterError,
  shouldRetryBasicAudioCapture,
} from "@/lib/browserVoiceCapabilities";
import {
  getWakeWordModeLabel,
  normalizeVoiceState,
  normalizeWakeWordMode,
  VOICE_STATES,
  VOICE_STATE_DISPLAY,
  WAKE_WORD_MODES,
} from "@/constants/voiceRuntime";

const WAKE_WORD = "aion";
const VOICE_PROFILE_TO_TTS = { female: "nova", male: "onyx" };
const WAKE_WINDOW_MS = 8000;
const FOLLOW_UP_WINDOW_MS = 10000;
const NO_AUDIO_TIMEOUT_MS = 4500;
const MAX_RECORDING_TIMEOUT_MS = 9000;
const SILENCE_STOP_MS = 1200;
const IOS_NO_AUDIO_TIMEOUT_MS = 6500;
const IOS_MAX_RECORDING_TIMEOUT_MS = 12000;
const IOS_SILENCE_STOP_MS = 1800;
const RMS_SPEECH_THRESHOLD = 0.035;
const TRANSCRIBING_TIMEOUT_MS = 15000;
const THINKING_TIMEOUT_MS = 18000;
const RESPONDING_TIMEOUT_MS = 20000;
const SPEAKING_TIMEOUT_MS = 300000;
const MEDIA_ELEMENT_UNKNOWN_DURATION_TIMEOUT_MS = 90000;
const WEB_AUDIO_END_GRACE_MS = 1500;
const SILENT_AUDIO_DATA_URL = "data:audio/wav;base64,UklGRlQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YTAAAAA=";

const createVoiceSessionId = () => `voice-session-${Math.random().toString(36).slice(2, 10)}`;
const voiceSessionStorageKey = (profileUserId) => `aion_voice_session_id:${String(profileUserId || "default")}`;
const getOrCreateProfileVoiceSessionId = (profileUserId) => {
  const key = voiceSessionStorageKey(profileUserId);
  const existing = window.localStorage.getItem(key);
  if (existing) return existing;
  const created = createVoiceSessionId();
  window.localStorage.setItem(key, created);
  return created;
};

const createDefaultVoiceStatus = () => ({
  voice_ready: false,
  stt_ready: false,
  tts_ready: false,
  wake_word_ready: false,
  wake_word: "AION",
  wake_word_mode: WAKE_WORD_MODES.DISABLED,
  wake_word_provider: WAKE_WORD_MODES.DISABLED,
  supported_wake_word_modes: Object.values(WAKE_WORD_MODES),
  voice_state: VOICE_STATES.IDLE,
  display_state: "Voice wird geprüft",
  stt_provider: "groq",
  tts_provider: "cartesia",
  stt_streaming_ready: false,
  tts_streaming_ready: false,
  fallback_used: false,
  interruption_count: 0,
  partial_transcript: "",
  error_code: "",
  privacy: { mic: "MIC_OFF", listening: false, notice: "Mikrofon ist aus." },
  latency: {},
  session_focus: {},
  room_context: {},
  fast_path_used: false,
});

const mergeVoiceStatus = (current, next) => {
  const merged = {
    ...current,
    ...(next || {}),
    voice_state: normalizeVoiceState(next?.voice_state || current?.voice_state),
    wake_word_mode: normalizeWakeWordMode(next?.wake_word_mode || current?.wake_word_mode),
    privacy: { ...(current?.privacy || {}), ...(next?.privacy || {}) },
    latency: { ...(current?.latency || {}), ...(next?.latency || {}) },
    session_focus: { ...(current?.session_focus || {}), ...(next?.session_focus || {}) },
    room_context: { ...(current?.room_context || {}), ...(next?.room_context || {}) },
  };
  merged.display_state = next?.display_state || VOICE_STATE_DISPLAY[merged.voice_state] || merged.voice_state;
  merged.wake_word_provider = next?.wake_word_provider || current?.wake_word_provider || merged.wake_word_mode;
  return merged;
};

const blobToBase64 = async (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(String(reader.result || ""));
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const stopWords = new Set(["stopp", "stop", "ruhe", "abbrechen", "cancel"]);

const languageCodeForProvider = (languageCode) => String(languageCode || "de").toLowerCase().split(/[-_]/)[0];

const audioMimeTypeForFormat = (format) => ({
  mp3: "audio/mpeg",
  mpeg: "audio/mpeg",
  wav: "audio/wav",
  webm: "audio/webm",
  mp4: "audio/mp4",
  aac: "audio/aac",
  opus: "audio/opus",
  flac: "audio/flac",
}[String(format || "mp3").toLowerCase()] || "audio/mpeg");

const parseAudioDataUrl = (value) => {
  const match = String(value || "").match(/^data:(audio\/[^;]+);base64,(.+)$/i);
  if (!match) return null;
  return { mimeType: match[1], base64: match[2] };
};

const createObjectUrlFromBase64Audio = (base64Value, format = "mp3", explicitMimeType) => {
  const parsed = parseAudioDataUrl(base64Value);
  const base64 = parsed?.base64 || String(base64Value || "").trim();
  if (!base64) return null;
  const mimeType = explicitMimeType || parsed?.mimeType || audioMimeTypeForFormat(format);
  const binary = window.atob(base64);
  const chunkSize = 1024;
  const chunks = [];
  for (let index = 0; index < binary.length; index += chunkSize) {
    const slice = binary.slice(index, index + chunkSize);
    const bytes = new Uint8Array(slice.length);
    for (let offset = 0; offset < slice.length; offset += 1) {
      bytes[offset] = slice.charCodeAt(offset);
    }
    chunks.push(bytes);
  }
  return URL.createObjectURL(new Blob(chunks, { type: mimeType }));
};

const voiceCaptureErrorCode = (error) => {
  const name = String(error?.name || "");
  if (["NotAllowedError", "SecurityError"].includes(name)) return "MIC_PERMISSION_DENIED";
  if (["NotFoundError", "DevicesNotFoundError"].includes(name)) return "MIC_NOT_FOUND";
  if (name === "NotReadableError") return "MIC_NOT_READABLE";
  if (["OverconstrainedError", "ConstraintNotSatisfiedError"].includes(name)) return "MIC_CONSTRAINT_FAILED";
  return "MIC_CAPTURE_FAILED";
};

const VOICE_CLIENT_ERROR_COPY = {
  de: {
    retry: "AION ist gerade kurz nicht erreichbar. Bitte versuche es erneut.",
    mic: "Das Mikrofon ist nicht verfügbar. Erlaube den Zugriff oder nutze Text.",
    audio: "Ich konnte kein klares Audio erkennen. Bitte versuche es erneut.",
    speech: "Der Sprachdienst ist gerade nicht verfügbar. Nutze bitte Text oder versuche es erneut.",
    generic: "Voice ist gerade nicht verfügbar. Nutze bitte Text oder versuche es erneut.",
  },
  en: {
    retry: "AION is briefly unavailable. Please try again.",
    mic: "The microphone is unavailable. Allow access or use text.",
    audio: "I couldn't detect clear audio. Please try again.",
    speech: "The voice service is currently unavailable. Use text or try again.",
    generic: "Voice is currently unavailable. Use text or try again.",
  },
  sq: {
    retry: "AION nuk është i arritshëm për momentin. Të lutem provo përsëri.",
    mic: "Mikrofoni nuk është i disponueshëm. Lejo qasjen ose përdor tekst.",
    audio: "Nuk arrita të dalloj audio të qartë. Të lutem provo përsëri.",
    speech: "Shërbimi zanor nuk është i disponueshëm për momentin. Përdor tekst ose provo përsëri.",
    generic: "Voice nuk është i disponueshëm për momentin. Përdor tekst ose provo përsëri.",
  },
  tr: {
    retry: "AION şu anda kısa süreliğine kullanılamıyor. Lütfen tekrar deneyin.",
    mic: "Mikrofon kullanılamıyor. Erişime izin verin veya metin kullanın.",
    audio: "Net bir ses algılayamadım. Lütfen tekrar deneyin.",
    speech: "Ses hizmeti şu anda kullanılamıyor. Metin kullanın veya tekrar deneyin.",
    generic: "Voice şu anda kullanılamıyor. Metin kullanın veya tekrar deneyin.",
  },
  fr: {
    retry: "AION est momentan indisponible. Réessaie, s'il te plaît.",
    mic: "Le microphone n'est pas disponible. Autorise l'accès ou utilise le texte.",
    audio: "Je n'ai pas détecté un son suffisamment clair. Réessaie.",
    speech: "Le service vocal est momentanément indisponible. Utilise le texte ou réessaie.",
    generic: "La voix est momentanément indisponible. Utilise le texte ou réessaie.",
  },
  es: {
    retry: "AION no está disponible temporalmente. Inténtalo de nuevo.",
    mic: "El micrófono no está disponible. Permite el acceso o usa texto.",
    audio: "No pude detectar un audio claro. Inténtalo de nuevo.",
    speech: "El servicio de voz no está disponible ahora. Usa texto o inténtalo de nuevo.",
    generic: "La voz no está disponible ahora. Usa texto o inténtalo de nuevo.",
  },
  it: {
    retry: "AION non è momentaneamente disponibile. Riprova.",
    mic: "Il microfono non è disponibile. Consenti l'accesso o usa il testo.",
    audio: "Non ho rilevato un audio chiaro. Riprova.",
    speech: "Il servizio vocale non è disponibile al momento. Usa il testo o riprova.",
    generic: "La voce non è disponibile al momento. Usa il testo o riprova.",
  },
  ar: {
    retry: "AION غير متاح مؤقتًا. يرجى المحاولة مرة أخرى.",
    mic: "الميكروفون غير متاح. اسمح بالوصول أو استخدم النص.",
    audio: "لم أتمكن من اكتشاف صوت واضح. يرجى المحاولة مرة أخرى.",
    speech: "الخدمة الصوتية غير متاحة حاليًا. استخدم النص أو حاول مرة أخرى.",
    generic: "الصوت غير متاح حاليًا. استخدم النص أو حاول مرة أخرى.",
  },
  nl: {
    retry: "AION is tijdelijk niet beschikbaar. Probeer het opnieuw.",
    mic: "De microfoon is niet beschikbaar. Sta toegang toe of gebruik tekst.",
    audio: "Ik kon geen duidelijke audio herkennen. Probeer het opnieuw.",
    speech: "De spraakdienst is momenteel niet beschikbaar. Gebruik tekst of probeer opnieuw.",
    generic: "Voice is momenteel niet beschikbaar. Gebruik tekst of probeer opnieuw.",
  },
  pt: {
    retry: "O AION está temporariamente indisponível. Tenta novamente.",
    mic: "O microfone não está disponível. Permite o acesso ou usa texto.",
    audio: "Não consegui detetar áudio claro. Tenta novamente.",
    speech: "O serviço de voz está indisponível neste momento. Usa texto ou tenta novamente.",
    generic: "A voz está indisponível neste momento. Usa texto ou tenta novamente.",
  },
  pl: {
    retry: "AION jest chwilowo niedostępny. Spróbuj ponownie.",
    mic: "Mikrofon jest niedostępny. Zezwól na dostęp albo użyj tekstu.",
    audio: "Nie udało mi się rozpoznać wyraźnego dźwięku. Spróbuj ponownie.",
    speech: "Usługa głosowa jest teraz niedostępna. Użyj tekstu albo spróbuj ponownie.",
    generic: "Voice jest teraz niedostępny. Użyj tekstu albo spróbuj ponownie.",
  },
  ru: {
    retry: "AION временно недоступен. Пожалуйста, попробуйте ещё раз.",
    mic: "Микрофон недоступен. Разрешите доступ или используйте текст.",
    audio: "Не удалось распознать чёткий звук. Попробуйте ещё раз.",
    speech: "Голосовой сервис сейчас недоступен. Используйте текст или попробуйте ещё раз.",
    generic: "Голос сейчас недоступен. Используйте текст или попробуйте ещё раз.",
  },
  sv: {
    retry: "AION är tillfälligt otillgänglig. Försök igen.",
    mic: "Mikrofonen är inte tillgänglig. Tillåt åtkomst eller använd text.",
    audio: "Jag kunde inte uppfatta tydligt ljud. Försök igen.",
    speech: "Rösttjänsten är inte tillgänglig just nu. Använd text eller försök igen.",
    generic: "Voice är inte tillgängligt just nu. Använd text eller försök igen.",
  },
  no: {
    retry: "AION er midlertidig utilgjengelig. Prøv igjen.",
    mic: "Mikrofonen er ikke tilgjengelig. Tillat tilgang eller bruk tekst.",
    audio: "Jeg kunne ikke registrere tydelig lyd. Prøv igjen.",
    speech: "Stemmetjenesten er ikke tilgjengelig akkurat nå. Bruk tekst eller prøv igjen.",
    generic: "Voice er ikke tilgjengelig akkurat nå. Bruk tekst eller prøv igjen.",
  },
  da: {
    retry: "AION er midlertidigt utilgængelig. Prøv igen.",
    mic: "Mikrofonen er ikke tilgængelig. Tillad adgang eller brug tekst.",
    audio: "Jeg kunne ikke registrere tydelig lyd. Prøv igen.",
    speech: "Stemmetjenesten er ikke tilgængelig lige nu. Brug tekst eller prøv igen.",
    generic: "Voice er ikke tilgængelig lige nu. Brug tekst eller prøv igen.",
  },
  fi: {
    retry: "AION ei ole hetkellisesti käytettävissä. Yritä uudelleen.",
    mic: "Mikrofoni ei ole käytettävissä. Salli käyttö tai käytä tekstiä.",
    audio: "En pystynyt tunnistamaan selkeää ääntä. Yritä uudelleen.",
    speech: "Puhepalvelu ei ole juuri nyt käytettävissä. Käytä tekstiä tai yritä uudelleen.",
    generic: "Voice ei ole juuri nyt käytettävissä. Käytä tekstiä tai yritä uudelleen.",
  },
  el: {
    retry: "Το AION δεν είναι προσωρινά διαθέσιμο. Δοκίμασε ξανά.",
    mic: "Το μικρόφωνο δεν είναι διαθέσιμο. Επίτρεψε την πρόσβαση ή χρησιμοποίησε κείμενο.",
    audio: "Δεν μπόρεσα να αναγνωρίσω καθαρό ήχο. Δοκίμασε ξανά.",
    speech: "Η φωνητική υπηρεσία δεν είναι διαθέσιμη αυτή τη στιγμή. Χρησιμοποίησε κείμενο ή δοκίμασε ξανά.",
    generic: "Η φωνή δεν είναι διαθέσιμη αυτή τη στιγμή. Χρησιμοποίησε κείμενο ή δοκίμασε ξανά.",
  },
  sr: {
    retry: "AION trenutno nije dostupan. Pokušaj ponovo.",
    mic: "Mikrofon nije dostupan. Dozvoli pristup ili koristi tekst.",
    audio: "Nisam uspeo da prepoznam jasan zvuk. Pokušaj ponovo.",
    speech: "Glasovna usluga trenutno nije dostupna. Koristi tekst ili pokušaj ponovo.",
    generic: "Voice trenutno nije dostupan. Koristi tekst ili pokušaj ponovo.",
  },
  hr: {
    retry: "AION trenutačno nije dostupan. Pokušaj ponovno.",
    mic: "Mikrofon nije dostupan. Dopusti pristup ili koristi tekst.",
    audio: "Nisam uspio prepoznati jasan zvuk. Pokušaj ponovno.",
    speech: "Glasovna usluga trenutačno nije dostupna. Koristi tekst ili pokušaj ponovno.",
    generic: "Voice trenutačno nije dostupan. Koristi tekst ili pokušaj ponovno.",
  },
  bs: {
    retry: "AION trenutno nije dostupan. Pokušaj ponovo.",
    mic: "Mikrofon nije dostupan. Dozvoli pristup ili koristi tekst.",
    audio: "Nisam uspio prepoznati jasan zvuk. Pokušaj ponovo.",
    speech: "Glasovna usluga trenutno nije dostupna. Koristi tekst ili pokušaj ponovo.",
    generic: "Voice trenutno nije dostupan. Koristi tekst ili pokušaj ponovo.",
  },
  ro: {
    retry: "AION este temporar indisponibil. Încearcă din nou.",
    mic: "Microfonul nu este disponibil. Permite accesul sau folosește text.",
    audio: "Nu am putut detecta un sunet clar. Încearcă din nou.",
    speech: "Serviciul vocal nu este disponibil momentan. Folosește text sau încearcă din nou.",
    generic: "Voice nu este disponibil momentan. Folosește text sau încearcă din nou.",
  },
  bg: {
    retry: "AION временно не е достъпен. Моля, опитай отново.",
    mic: "Микрофонът не е достъпен. Разреши достъпа или използвай текст.",
    audio: "Не успях да разпозная ясен звук. Моля, опитай отново.",
    speech: "Гласовата услуга в момента не е достъпна. Използвай текст или опитай отново.",
    generic: "Voice в момента не е достъпен. Използвай текст или опитай отново.",
  },
  zh: {
    retry: "AION 暂时不可用。请重试。",
    mic: "麦克风不可用。请允许访问或使用文字。",
    audio: "我没有检测到清晰的音频。请重试。",
    speech: "语音服务当前不可用。请使用文字或重试。",
    generic: "语音当前不可用。请使用文字或重试。",
  },
  ja: {
    retry: "AION は一時的に利用できません。もう一度お試しください。",
    mic: "マイクを利用できません。アクセスを許可するか、テキストを使用してください。",
    audio: "明瞭な音声を検出できませんでした。もう一度お試しください。",
    speech: "音声サービスは現在利用できません。テキストを使用するか、もう一度お試しください。",
    generic: "音声機能は現在利用できません。テキストを使用するか、もう一度お試しください。",
  },
  ko: {
    retry: "AION을 일시적으로 사용할 수 없습니다. 다시 시도해 주세요.",
    mic: "마이크를 사용할 수 없습니다. 접근을 허용하거나 텍스트를 사용해 주세요.",
    audio: "명확한 오디오를 감지하지 못했습니다. 다시 시도해 주세요.",
    speech: "음성 서비스를 현재 사용할 수 없습니다. 텍스트를 사용하거나 다시 시도해 주세요.",
    generic: "음성을 현재 사용할 수 없습니다. 텍스트를 사용하거나 다시 시도해 주세요.",
  },
  uk: {
    retry: "AION тимчасово недоступний. Спробуйте ще раз.",
    mic: "Мікрофон недоступний. Дозвольте доступ або використайте текст.",
    audio: "Не вдалося розпізнати чіткий звук. Спробуйте ще раз.",
    speech: "Голосовий сервіс зараз недоступний. Використайте текст або спробуйте ще раз.",
    generic: "Голос зараз недоступний. Використайте текст або спробуйте ще раз.",
  },
  hi: {
    retry: "AION अभी अस्थायी रूप से उपलब्ध नहीं है। कृपया फिर प्रयास करें।",
    mic: "माइक्रोफ़ोन उपलब्ध नहीं है। अनुमति दें या टेक्स्ट का उपयोग करें।",
    audio: "मुझे स्पष्ट ऑडियो नहीं मिला। कृपया फिर प्रयास करें।",
    speech: "वॉइस सेवा अभी उपलब्ध नहीं है। टेक्स्ट का उपयोग करें या फिर प्रयास करें।",
    generic: "वॉइस अभी उपलब्ध नहीं है। टेक्स्ट का उपयोग करें या फिर प्रयास करें।",
  },
  th: {
    retry: "AION ไม่พร้อมใช้งานชั่วคราว โปรดลองอีกครั้ง",
    mic: "ไม่สามารถใช้ไมโครโฟนได้ โปรดอนุญาตการเข้าถึงหรือใช้ข้อความ",
    audio: "ไม่พบเสียงที่ชัดเจน โปรดลองอีกครั้ง",
    speech: "บริการเสียงไม่พร้อมใช้งานในขณะนี้ โปรดใช้ข้อความหรือลองอีกครั้ง",
    generic: "เสียงไม่พร้อมใช้งานในขณะนี้ โปรดใช้ข้อความหรือลองอีกครั้ง",
  },
  he: {
    retry: "AION אינו זמין זמנית. נסה שוב.",
    mic: "המיקרופון אינו זמין. אפשר גישה או השתמש בטקסט.",
    audio: "לא זוהה שמע ברור. נסה שוב.",
    speech: "שירות הקול אינו זמין כרגע. השתמש בטקסט או נסה שוב.",
    generic: "הקול אינו זמין כרגע. השתמש בטקסט או נסה שוב.",
  },
  fa: {
    retry: "AION موقتاً در دسترس نیست. دوباره تلاش کنید.",
    mic: "میکروفون در دسترس نیست. دسترسی را مجاز کنید یا از متن استفاده کنید.",
    audio: "صدای واضحی تشخیص داده نشد. دوباره تلاش کنید.",
    speech: "سرویس صوتی فعلاً در دسترس نیست. از متن استفاده کنید یا دوباره تلاش کنید.",
    generic: "صدا فعلاً در دسترس نیست. از متن استفاده کنید یا دوباره تلاش کنید.",
  },
  ur: {
    retry: "AION عارضی طور پر دستیاب نہیں ہے۔ دوبارہ کوشش کریں۔",
    mic: "مائیکروفون دستیاب نہیں ہے۔ رسائی کی اجازت دیں یا متن استعمال کریں۔",
    audio: "واضح آڈیو کا پتہ نہیں چل سکا۔ دوبارہ کوشش کریں۔",
    speech: "وائس سروس فی الحال دستیاب نہیں ہے۔ متن استعمال کریں یا دوبارہ کوشش کریں۔",
    generic: "وائس فی الحال دستیاب نہیں ہے۔ متن استعمال کریں یا دوبارہ کوشش کریں۔",
  },
  bn: {
    retry: "AION সাময়িকভাবে উপলভ্য নয়। আবার চেষ্টা করুন।",
    mic: "মাইক্রোফোন উপলভ্য নয়। অনুমতি দিন অথবা টেক্সট ব্যবহার করুন।",
    audio: "স্পষ্ট অডিও শনাক্ত করা যায়নি। আবার চেষ্টা করুন।",
    speech: "ভয়েস পরিষেবা এখন উপলভ্য নয়। টেক্সট ব্যবহার করুন অথবা আবার চেষ্টা করুন।",
    generic: "ভয়েস এখন উপলভ্য নয়। টেক্সট ব্যবহার করুন অথবা আবার চেষ্টা করুন।",
  },
};

const voiceClientErrorCopy = (languageCode, key) => {
  const base = languageCodeForProvider(languageCode);
  const copy = VOICE_CLIENT_ERROR_COPY[base] || VOICE_CLIENT_ERROR_COPY.en;
  return copy[key] || copy.generic;
};

const normalizeVoiceError = (error, languageCode = "de") => {
  const message = String(error?.message || "");
  const name = error?.name || "";
  const status = Number(error?.status || 0);
  if ([502, 503, 504].includes(status) || /<(?:!doctype|html|body|head|script|div|title)[\s>]/i.test(message)) {
    return voiceClientErrorCopy(languageCode, "retry");
  }
  if (message.includes("Microphone access is disabled in Trust Center")) {
    return voiceClientErrorCopy(languageCode, "mic");
  }
  if (message.includes("MIC_PERMISSION_DENIED") || name === "NotAllowedError" || name === "SecurityError") {
    return voiceClientErrorCopy(languageCode, "mic");
  }
  if (message.includes("NO_AUDIO")) {
    return voiceClientErrorCopy(languageCode, "audio");
  }
  if (message.includes("STT_FAILED") || message.includes("TTS_FAILED") || message.includes("PROVIDER_UNAVAILABLE")) {
    return voiceClientErrorCopy(languageCode, "speech");
  }
  if (message.includes("NETWORK_ERROR") || status === 408 || message.includes("zu lange") || message.includes("taking too long")) {
    return voiceClientErrorCopy(languageCode, "retry");
  }
  if (name === "NotFoundError" || name === "DevicesNotFoundError" || name === "NotReadableError") {
    return voiceClientErrorCopy(languageCode, "mic");
  }
  return message || voiceClientErrorCopy(languageCode, "generic");
};

const browserLocaleFor = (languageCode) => {
  const code = (languageCode || "de").toLowerCase();
  return {
    de: "de-DE",
    en: "en-US",
    sq: "sq-AL",
    tr: "tr-TR",
    fr: "fr-FR",
    es: "es-ES",
    it: "it-IT",
    ar: "ar-SA",
    nl: "nl-NL",
    pt: "pt-PT",
    pl: "pl-PL",
    ru: "ru-RU",
    sv: "sv-SE",
    no: "nb-NO",
    da: "da-DK",
    fi: "fi-FI",
    el: "el-GR",
    zh: "zh-CN",
    sr: "sr-RS",
    bs: "bs-BA",
    hr: "hr-HR",
    ro: "ro-RO",
    bg: "bg-BG",
    ja: "ja-JP",
    ko: "ko-KR",
    uk: "uk-UA",
    cs: "cs-CZ",
    hu: "hu-HU",
    hi: "hi-IN",
    id: "id-ID",
    ms: "ms-MY",
    vi: "vi-VN",
    th: "th-TH",
    he: "he-IL",
    fa: "fa-IR",
    ur: "ur-PK",
    bn: "bn-BD",
    sw: "sw-KE",
  }[code] || `${code}-${code.toUpperCase()}`;
};

const VOICE_LANGUAGE_LABELS = {
  de: "Deutsch",
  en: "Englisch",
  sq: "Albanisch",
  tr: "Türkisch",
  fr: "Französisch",
  es: "Spanisch",
  it: "Italienisch",
  ar: "Arabisch",
  nl: "Niederländisch",
  pt: "Portugiesisch",
  pl: "Polnisch",
  ru: "Russisch",
  sv: "Schwedisch",
  no: "Norwegisch",
  da: "Dänisch",
  fi: "Finnisch",
  el: "Griechisch",
  zh: "Chinesisch (vereinfacht)",
  sr: "Serbisch",
  bs: "Bosnisch",
  hr: "Kroatisch",
  ro: "Rumänisch",
  bg: "Bulgarisch",
  ja: "Japanisch",
  ko: "Koreanisch",
  uk: "Ukrainisch",
  cs: "Tschechisch",
  hu: "Ungarisch",
  hi: "Hindi",
  id: "Indonesisch",
  ms: "Malaiisch",
  vi: "Vietnamesisch",
  th: "Thailändisch",
  he: "Hebräisch",
  fa: "Persisch",
  ur: "Urdu",
  bn: "Bengalisch",
  sw: "Swahili",
};

const normalizeSelectedLanguages = (values) => {
  const next = [];
  for (const value of values || []) {
    const normalized = languageCodeForProvider(value);
    if (!normalized || !VOICE_LANGUAGE_LABELS[normalized] || next.includes(normalized)) continue;
    next.push(normalized);
    if (next.length >= 38) break;
  }
  return next;
};

const languageSelectionSummary = (values) => {
  const normalized = normalizeSelectedLanguages(values);
  if (normalized.length > 3) return `${normalized.length} Sprachen`;
  return normalized
    .map((item) => VOICE_LANGUAGE_LABELS[item] || item.toUpperCase())
    .join(" + ");
};

const singleLanguageLabel = (value) => VOICE_LANGUAGE_LABELS[languageCodeForProvider(value)] || languageCodeForProvider(value)?.toUpperCase() || "Deutsch";

const buildMemoryLearnedNotice = (result) => {
  if (result?.intent !== "memory_save") return null;
  return String(result?.response || "").trim() || "AION hat sich diesen Fakt gemerkt.";
};

const detectTranscriptLanguage = (text, fallback = "en") => {
  const lowered = String(text || "").toLowerCase().trim();
  if (!lowered) return languageCodeForProvider(fallback);
  if (lowered.includes("mirëmëngjes") || lowered.includes("miremengjes") || lowered.includes("çfarë") || lowered.includes("cfare") || lowered.includes("mban mend") || lowered.includes("faleminderit")) return "sq";
  if (/[ğış]/.test(lowered) || lowered.includes("günaydın") || lowered.includes("bugün") || lowered.includes("hava durumu") || lowered.includes("teşekkür") || lowered.includes("nasılsın")) return "tr";
  if (lowered.includes("guten morgen") || lowered.includes("guten abend") || lowered.includes("wie geht") || lowered.includes("wetter") || lowered.includes("ich brauche") || lowered.includes("wie ist") || lowered.includes("öffne") || lowered.includes("für")) return "de";
  if (lowered.includes("good morning") || lowered.includes("good evening") || lowered.includes("how are") || lowered.includes("weather") || lowered.includes("what did i")) return "en";
  if (lowered.includes("bonjour") || lowered.includes("merci") || lowered.includes("s'il vous plaît")) return "fr";
  if (lowered.includes("hola") || lowered.includes("gracias") || lowered.includes("buenos días") || lowered.includes("buenos dias")) return "es";
  if (lowered.includes("ciao") || lowered.includes("grazie") || lowered.includes("per favore")) return "it";
  if (lowered.includes("obrigado") || lowered.includes("obrigada") || lowered.includes("bom dia")) return "pt";
  if (lowered.includes("dank je") || lowered.includes("alsjeblieft") || lowered.includes("goedemorgen")) return "nl";
  if (lowered.includes("dziękuję") || lowered.includes("dziekuje") || lowered.includes("proszę") || lowered.includes("prosze")) return "pl";
  if (lowered.includes("tack") || lowered.includes("god morgon")) return "sv";
  if (lowered.includes("takk") || lowered.includes("god morgen")) return "no";
  if (lowered.includes("tak") || lowered.includes("godmorgen")) return "da";
  if (lowered.includes("kiitos") || lowered.includes("hyvää huomenta") || lowered.includes("hyvaa huomenta")) return "fi";
  if (/[α-ωάέήίόύώϊϋΐΰΑ-Ω]/.test(text)) return "el";
  if (/[\u3040-\u30ff]/.test(text)) return "ja";
  if (/[\uac00-\ud7af]/.test(text)) return "ko";
  if (/[\u0900-\u097F]/.test(text)) return "hi";
  if (/[\u0980-\u09FF]/.test(text)) return "bn";
  if (/[\u0E00-\u0E7F]/.test(text)) return "th";
  if (/[\u0590-\u05FF]/.test(text)) return "he";
  if (/[پچژگک‌ی]/.test(text)) return "fa";
  if (/[ٹڈڑںھہےے]/.test(text)) return "ur";
  if (/[іїєґІЇЄҐ]/.test(text)) return "uk";
  if (/[ъЪ]/.test(text) || lowered.includes("здравей") || lowered.includes("светлина") || lowered.includes("включи") || lowered.includes("изключи")) return "bg";
  if (lowered.includes("здраво") || lowered.includes("светло") || lowered.includes("упали") || lowered.includes("угаси")) return "sr";
  if (/[а-яА-Я]/.test(text)) return "ru";
  if (/[ء-ي]/.test(text)) return "ar";
  if (/[\u4e00-\u9fff]/.test(text)) return "zh";
  return languageCodeForProvider(fallback);
};

const isTranscriptCompatibleWithLockedLanguage = (text, languageCode) => {
  const normalized = languageCodeForProvider(languageCode);
  const sample = String(text || "").trim();
  if (!sample) return true;
  if (/[\u3040-\u30ff]/.test(sample)) return normalized === "ja";
  if (/[\uac00-\ud7af]/.test(sample)) return normalized === "ko";
  if (/[\u0900-\u097F]/.test(sample)) return normalized === "hi";
  if (/[\u0980-\u09FF]/.test(sample)) return normalized === "bn";
  if (/[\u0E00-\u0E7F]/.test(sample)) return normalized === "th";
  if (/[\u0590-\u05FF]/.test(sample)) return normalized === "he";
  if (/[پچژگک‌ی]/.test(sample)) return normalized === "fa";
  if (/[ٹڈڑںھہےے]/.test(sample)) return normalized === "ur";
  if (/[іїєґІЇЄҐ]/.test(sample)) return normalized === "uk";
  if (/[\u0400-\u04FF]/.test(sample)) return ["ru", "bg", "sr", "uk"].includes(normalized);
  if (/[\u0600-\u06FF]/.test(sample)) return ["ar", "fa", "ur"].includes(normalized);
  if (/[\u4E00-\u9FFF]/.test(sample)) return normalized === "zh";
  if (/[α-ωάέήίόύώϊϋΐΰΑ-Ω]/.test(sample)) return normalized === "el";
  const lowered = sample.toLowerCase();
  const englishSignals = lowered.match(/\b(here'?s|the|this|that|please|could|would|should|you|your|mean|mesh|first|image|hello|what|where|how|today|tomorrow|thank|thanks)\b/g) || [];
  if (normalized !== "en" && englishSignals.length >= 2) return false;
  if (normalized === "de" && /\b(first|image|hello|please|thank|what|where|how|today|tomorrow|here'?s|mesh)\b/.test(lowered)) return false;
  return true;
};

const TRANSCRIPT_MISMATCH_MESSAGES = {
  de: "Ich habe dich gerade nicht sauber auf Deutsch verstanden. Bitte versuch es noch einmal.",
  en: "I couldn't clearly understand that in English. Please try again.",
  sq: "Nuk të kuptova qartë në shqip. Të lutem provoje edhe një herë.",
  tr: "Seni Türkçe olarak net anlayamadım. Lütfen tekrar deneyin.",
  fr: "Je ne t'ai pas clairement compris en français. Réessaie, s'il te plaît.",
  es: "No te entendí claramente en español. Inténtalo de nuevo, por favor.",
  it: "Non ti ho capito chiaramente in italiano. Per favore, riprova.",
  ar: "لم أفهمك بوضوح باللغة العربية. يرجى المحاولة مرة أخرى.",
  nl: "Ik heb je niet duidelijk in het Nederlands verstaan. Probeer het opnieuw.",
  pt: "Não te entendi claramente em português. Tenta novamente, por favor.",
  pl: "Nie zrozumiałem Cię wyraźnie po polsku. Spróbuj ponownie.",
  ru: "Я не смог чётко понять вас по-русски. Пожалуйста, попробуйте ещё раз.",
  sv: "Jag förstod dig inte tydligt på svenska. Försök igen.",
  no: "Jeg forsto deg ikke tydelig på norsk. Prøv igjen.",
  da: "Jeg forstod dig ikke tydeligt på dansk. Prøv igen.",
  fi: "En ymmärtänyt sinua selvästi suomeksi. Yritä uudelleen.",
  el: "Δεν σε κατάλαβα καθαρά στα ελληνικά. Δοκίμασε ξανά.",
  sr: "Nisam te jasno razumeo na srpskom. Pokušaj ponovo.",
  hr: "Nisam te jasno razumio na hrvatskom. Pokušaj ponovno.",
  bs: "Nisam te jasno razumio na bosanskom. Pokušaj ponovo.",
  ro: "Nu te-am înțeles clar în română. Te rog încearcă din nou.",
  bg: "Не те разбрах ясно на български. Моля, опитай отново.",
  zh: "我没有清楚地听懂你所说的中文。请再试一次。",
  ja: "日本語としてはっきり聞き取れませんでした。もう一度お試しください。",
  ko: "한국어로 명확하게 이해하지 못했습니다. 다시 시도해 주세요.",
  uk: "Я не зміг чітко зрозуміти вас українською. Спробуйте ще раз.",
  hi: "मैं आपको हिंदी में स्पष्ट रूप से समझ नहीं पाया। कृपया फिर प्रयास करें।",
  th: "ฉันฟังภาษาไทยได้ไม่ชัดเจน โปรดลองอีกครั้ง",
  he: "לא הבנתי אותך בבירור בעברית. נסה שוב.",
  fa: "صحبت شما را به فارسی واضح متوجه نشدم. دوباره تلاش کنید.",
  ur: "میں آپ کو اردو میں واضح طور پر نہیں سمجھ سکا۔ دوبارہ کوشش کریں۔",
  bn: "আমি আপনাকে বাংলায় স্পষ্টভাবে বুঝতে পারিনি। আবার চেষ্টা করুন।",
};

const transcriptMismatchMessage = (languageCode) => {
  const normalized = languageCodeForProvider(languageCode);
  return TRANSCRIPT_MISMATCH_MESSAGES[normalized] || TRANSCRIPT_MISMATCH_MESSAGES.en;
};

const isStopCommand = (text) => stopWords.has((text || "").trim().toLowerCase());

const cleanWakeWordCommand = (text) => String(text || "")
  .replace(/^aion[\s,:;.!?-]*/i, "")
  .trim();

const providerForMode = (mode) => {
  const normalized = normalizeWakeWordMode(mode);
  if (normalized === WAKE_WORD_MODES.BROWSER) return WAKE_WORD_MODES.BROWSER;
  if (normalized === WAKE_WORD_MODES.LOCAL) return WAKE_WORD_MODES.LOCAL;
  return WAKE_WORD_MODES.DISABLED;
};

const normalizeAudioLevel = (rms) => Math.max(0, Math.min(1, rms / 0.18));

const resumeAudioContextIfNeeded = async (context) => {
  if (!context || typeof context.resume !== "function") return;
  if (context.state && context.state !== "running" && context.state !== "closed") {
    await context.resume().catch(() => undefined);
  }
};

export const useAionVoiceWorkspace = ({ token, memory, onRefresh, activeProfileUserId, trustPrivacyKey }) => {
  const [voiceCommand, setVoiceCommand] = useState("");
  const [voiceResult, setVoiceResult] = useState(null);
  const [voiceDisplay, setVoiceDisplay] = useState({ response: "", language: "", intent: "", wakeWordDetected: false, transcript: "", memoryNotice: "" });
  const [voicePending, setVoicePending] = useState(false);
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const [voiceInputMode, setVoiceInputMode] = useState("text");
  const [wakeWordArmed, setWakeWordArmed] = useState(false);
  const [wakeWordSupported, setWakeWordSupported] = useState(false);
  const [wakeWordHeard, setWakeWordHeard] = useState("");
  const [voiceStatus, setVoiceStatus] = useState(createDefaultVoiceStatus());
  const [voiceSessionId, setVoiceSessionId] = useState(() => getOrCreateProfileVoiceSessionId(activeProfileUserId));
  const [memoryRecallResult, setMemoryRecallResult] = useState(null);
  const [selectedVoiceLanguages, setSelectedVoiceLanguages] = useState([]);
  const [assistantVoiceProfile, setAssistantVoiceProfile] = useState(() => memory?.assistant_voice_profile || "female");
  const [voiceMuted, setVoiceMuted] = useState(false);
  const [handsFreeMicEnabled, setHandsFreeMicEnabled] = useState(false);
  const [audioInputLevel, setAudioInputLevel] = useState(0);
  const [audioOutputLevel, setAudioOutputLevel] = useState(0);
  const [vadSupported, setVadSupported] = useState(false);

  const mediaRecorderRef = useRef(null);
  const audioFileInputRef = useRef(null);
  const recognitionRef = useRef(null);
  const manualRecognitionActiveRef = useRef(false);
  const wakeWordEnabledRef = useRef(false);
  const wakeWordBusyRef = useRef(false);
  const wakeWordBusyTimerRef = useRef(null);
  const lastHandsFreeTranscriptRef = useRef("");
  const lastHandsFreeTranscriptAtRef = useRef(0);
  const recordedChunksRef = useRef([]);
  const currentAudioRef = useRef(null);
  const wakeWindowTimerRef = useRef(null);
  const wakeAwaitingCommandRef = useRef(false);
  const mediaStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const vadFrameRef = useRef(null);
  const noAudioTimerRef = useRef(null);
  const maxRecordingTimerRef = useRef(null);
  const activeMicTurnRef = useRef(null);
  const recordingStopReasonRef = useRef("manual_stop");
  const lastWakeDetectedAtRef = useRef(0);
  const outputAudioContextRef = useRef(null);
  const outputAnalyserRef = useRef(null);
  const outputSourceRef = useRef(null);
  const outputObjectUrlRef = useRef(null);
  const outputFrameRef = useRef(null);
  const outputMediaElementSourceRef = useRef(null);
  const outputPlaybackGenerationRef = useRef(0);
  const stateTimeoutRef = useRef(null);
  const streamingReplyActiveRef = useRef(false);
  const streamedChunkBoundaryRef = useRef(0);
  const streamedLanguageRef = useRef("de");
  const ttsQueueRef = useRef([]);
  const ttsQueueBusyRef = useRef(false);
  const ttsQueueGenerationRef = useRef(0);
  const finalStreamingReplyRef = useRef(false);
  const handsFreeMicEnabledRef = useRef(false);
  const handsFreeRestartTimerRef = useRef(null);
  const voicePendingRef = useRef(false);
  const voiceMutedRef = useRef(false);
  const assistantVoiceProfileSaveGenerationRef = useRef(0);
  const voiceLanguageSaveGenerationRef = useRef(0);
  const voicePreferenceSaveQueueRef = useRef(Promise.resolve());
  const voiceSessionProfileRef = useRef(String(activeProfileUserId || "default"));
  const voiceOperationIdRef = useRef(0);
  const activeRealtimeTurnIdRef = useRef(null);
  const voiceCaptureStartingRef = useRef(false);
  const nativeRecognitionUnsupportedLanguagesRef = useRef(new Set());
  const transitionToReadyStateRef = useRef(null);
  const startVoiceRecordingRef = useRef(null);

  const currentWakeMode = wakeWordArmed ? WAKE_WORD_MODES.BROWSER : WAKE_WORD_MODES.DISABLED;
  const currentRoomContext = useMemo(() => voiceStatus.room_context || {}, [voiceStatus.room_context]);

  const selectedPrimaryLanguage = useMemo(
    () => selectedVoiceLanguages[0] || memory?.preferred_language || memory?.preferred_languages?.[0] || "de",
    [memory?.preferred_language, memory?.preferred_languages, selectedVoiceLanguages],
  );
  const isAutoLanguageMode = useMemo(() => selectedVoiceLanguages.length !== 1, [selectedVoiceLanguages]);
  const allowedAutoLanguages = useMemo(
    () => normalizeSelectedLanguages(selectedVoiceLanguages.length ? selectedVoiceLanguages : (memory?.preferred_language ? [memory.preferred_language] : memory?.preferred_languages?.length ? memory.preferred_languages : [])),
    [memory?.preferred_language, memory?.preferred_languages, selectedVoiceLanguages],
  );
  const selectedPrimaryLocale = useMemo(() => browserLocaleFor(selectedPrimaryLanguage), [selectedPrimaryLanguage]);
  const selectedProviderLanguage = useMemo(() => languageCodeForProvider(selectedPrimaryLanguage), [selectedPrimaryLanguage]);
  const voiceLanguageMode = useMemo(() => (selectedVoiceLanguages.length === 1 ? "locked" : "auto"), [selectedVoiceLanguages]);
  const voiceLanguageModeLabel = useMemo(
    () => (selectedVoiceLanguages.length === 1
      ? `Fest gespeichert · ${languageSelectionSummary(selectedVoiceLanguages) || (VOICE_LANGUAGE_LABELS[selectedPrimaryLanguage] || selectedPrimaryLanguage.toUpperCase())}`
      : `Automatik zwischen ${languageSelectionSummary(selectedVoiceLanguages) || "Deutsch + Englisch"}`),
    [selectedPrimaryLanguage, selectedVoiceLanguages],
  );

  useEffect(() => {
    const stored = normalizeSelectedLanguages(memory?.preferred_languages || []);
    if (stored.length) {
      setSelectedVoiceLanguages(stored);
      return;
    }
    const primary = languageCodeForProvider(memory?.preferred_language || "de");
    setSelectedVoiceLanguages([primary || "de"]);
  }, [memory?.preferred_language, memory?.preferred_languages]);

  useEffect(() => {
    setAssistantVoiceProfile(memory?.assistant_voice_profile === "male" ? "male" : "female");
  }, [memory?.assistant_voice_profile]);

  useEffect(() => {
    wakeWordEnabledRef.current = wakeWordArmed;
  }, [wakeWordArmed]);

  useEffect(() => {
    handsFreeMicEnabledRef.current = handsFreeMicEnabled;
  }, [handsFreeMicEnabled]);

  useEffect(() => {
    voicePendingRef.current = voicePending;
  }, [voicePending]);

  useEffect(() => {
    voiceMutedRef.current = voiceMuted;
  }, [voiceMuted]);

  const applyVoiceSnapshot = useCallback((snapshot) => {
    if (!snapshot) return;
    setVoiceStatus((prev) => mergeVoiceStatus(prev, snapshot));
  }, []);

  const policyBlocked = Boolean(voiceStatus?.privacy?.policy_blocked);
  const voiceCaptureStrategy = useMemo(() => {
    if (typeof window === "undefined") return "unsupported";
    return resolveVoiceCaptureStrategy({
      navigatorLike: window.navigator || {},
      hasSpeechRecognition: Boolean(window.SpeechRecognition || window.webkitSpeechRecognition),
      hasMediaDevices: Boolean(window.navigator?.mediaDevices?.getUserMedia),
      hasMediaRecorder: typeof window.MediaRecorder !== "undefined",
    });
  }, []);
  const voiceCaptureSupported = voiceCaptureStrategy !== "unsupported";

  const setLocalVoiceState = useCallback((state, extras = {}) => {
    const normalizedState = normalizeVoiceState(state);
    setVoiceStatus((prev) => mergeVoiceStatus(prev, {
      voice_state: normalizedState,
      display_state: VOICE_STATE_DISPLAY[normalizedState] || normalizedState,
      wake_word_mode: extras.wake_word_mode || prev.wake_word_mode,
      wake_word_provider: extras.wake_word_provider || prev.wake_word_provider,
      ...extras,
    }));
  }, []);

  const syncRuntimeState = useCallback(async (payload, options = {}) => {
    try {
      const wakeWordMode = normalizeWakeWordMode(payload.wake_word_mode || currentWakeMode);
      const clientTurnId = payload?.metadata?.client_turn_id || activeRealtimeTurnIdRef.current || null;
      const snapshot = await syncVoiceState({
        session_id: voiceSessionId,
        ...payload,
        wake_word_mode: wakeWordMode,
        metadata: {
          ...(payload.metadata || {}),
          ...(clientTurnId ? { client_turn_id: clientTurnId } : {}),
          wake_word_provider: payload?.metadata?.wake_word_provider || providerForMode(wakeWordMode),
          room_context: payload?.metadata?.room_context || currentRoomContext,
          client_runtime: isCurrentIOSLikeBrowser() ? "ios" : "browser",
          hands_free: Boolean(handsFreeMicEnabledRef.current),
        },
      }, token);
      if (clientTurnId && clientTurnId !== activeRealtimeTurnIdRef.current) return null;
      applyVoiceSnapshot(snapshot);
      return snapshot;
    } catch (error) {
      if (!options.silent) {
        setVoiceError(normalizeVoiceError(error, selectedPrimaryLanguage));
      }
      return null;
    }
  }, [applyVoiceSnapshot, currentRoomContext, currentWakeMode, selectedPrimaryLanguage, token, voiceSessionId]);

  const clearWakeWindow = useCallback(() => {
    if (wakeWindowTimerRef.current) {
      window.clearTimeout(wakeWindowTimerRef.current);
      wakeWindowTimerRef.current = null;
    }
    wakeAwaitingCommandRef.current = false;
  }, []);

  const clearWakeWordBusy = useCallback(() => {
    if (wakeWordBusyTimerRef.current) {
      window.clearTimeout(wakeWordBusyTimerRef.current);
      wakeWordBusyTimerRef.current = null;
    }
    wakeWordBusyRef.current = false;
  }, []);

  const scheduleWakeWordBusyRelease = useCallback(() => {
    if (wakeWordBusyTimerRef.current) {
      window.clearTimeout(wakeWordBusyTimerRef.current);
    }
    wakeWordBusyTimerRef.current = window.setTimeout(() => {
      wakeWordBusyTimerRef.current = null;
      wakeWordBusyRef.current = false;
    }, 900);
  }, []);

  const clearRecordingResources = useCallback(({ keepStream = false } = {}) => {
    if (vadFrameRef.current) {
      window.cancelAnimationFrame(vadFrameRef.current);
      vadFrameRef.current = null;
    }
    if (noAudioTimerRef.current) {
      window.clearTimeout(noAudioTimerRef.current);
      noAudioTimerRef.current = null;
    }
    if (maxRecordingTimerRef.current) {
      window.clearTimeout(maxRecordingTimerRef.current);
      maxRecordingTimerRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => undefined);
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    if (!keepStream && mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    mediaRecorderRef.current = null;
    setAudioInputLevel(0);
  }, []);

  const clearHandsFreeRestart = useCallback(() => {
    if (handsFreeRestartTimerRef.current) {
      window.clearTimeout(handsFreeRestartTimerRef.current);
      handsFreeRestartTimerRef.current = null;
    }
  }, []);

  const resetManualMicMode = useCallback(() => {
    setHandsFreeMicEnabled(false);
    handsFreeMicEnabledRef.current = false;
    clearHandsFreeRestart();
    setVoiceInputMode("text");
  }, [clearHandsFreeRestart]);

  const clearOutputMediaElementSource = useCallback(() => {
    if (!outputMediaElementSourceRef.current) return;
    try {
      outputMediaElementSourceRef.current.disconnect?.();
    } catch {
      // ignore media-element source disconnect races
    }
    outputMediaElementSourceRef.current = null;
  }, []);

  const clearOutputAnalysis = useCallback(() => {
    outputPlaybackGenerationRef.current += 1;
    if (outputFrameRef.current) {
      window.cancelAnimationFrame(outputFrameRef.current);
      outputFrameRef.current = null;
    }
    if (outputSourceRef.current) {
      const source = outputSourceRef.current;
      try {
        source.onended = null;
      } catch {
        // ignore callback cleanup races
      }
      try {
        if (typeof source.stop === "function") source.stop(0);
      } catch {
        // ignore stop races
      }
      try {
        source.disconnect?.();
      } catch {
        // ignore disconnect races
      }
    }
    outputSourceRef.current = null;
    if (outputObjectUrlRef.current) {
      URL.revokeObjectURL(outputObjectUrlRef.current);
      outputObjectUrlRef.current = null;
    }
    setAudioOutputLevel(0);
  }, []);

  const ensureOutputContextUnlocked = useCallback(async ({ prime = false } = {}) => {
    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) return null;
    let context = outputAudioContextRef.current;
    let analyser = outputAnalyserRef.current;
    if (context?.state === "closed") {
      try {
        outputMediaElementSourceRef.current?.disconnect?.();
      } catch {
        // A closed Safari AudioContext may already have detached its nodes.
      }
      outputMediaElementSourceRef.current = null;
      outputAnalyserRef.current = null;
      outputAudioContextRef.current = null;
      context = null;
      analyser = null;
    }
    if (!context) {
      context = new AudioContextCtor();
      outputAudioContextRef.current = context;
    }
    if (!analyser) {
      analyser = context.createAnalyser();
      analyser.fftSize = 256;
      analyser.connect(context.destination);
      outputAnalyserRef.current = analyser;
    }
    await resumeAudioContextIfNeeded(context);
    if (prime) {
      try {
        const gain = context.createGain();
        gain.gain.value = 0;
        const buffer = context.createBuffer(1, 1, context.sampleRate || 44100);
        const source = context.createBufferSource();
        source.buffer = buffer;
        source.connect(gain);
        gain.connect(context.destination);
        source.start(0);
        source.stop(context.currentTime + 0.01);
      } catch {
        // priming is best effort only
      }
      try {
        const audio = currentAudioRef.current || new Audio();
        audio.preload = "auto";
        audio.playsInline = true;
        audio.onloadedmetadata = null;
        audio.muted = true;
        audio.src = SILENT_AUDIO_DATA_URL;
        currentAudioRef.current = audio;
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.then === "function") {
          let unlockTimeoutId;
          await Promise.race([
            playPromise.catch(() => undefined),
            new Promise((resolve) => {
              unlockTimeoutId = window.setTimeout(resolve, 250);
            }),
          ]);
          if (unlockTimeoutId) window.clearTimeout(unlockTimeoutId);
        }
        audio.pause();
        audio.currentTime = 0;
        audio.muted = false;
        audio.removeAttribute?.("src");
        audio.load?.();
      } catch {
        // media-element priming is also best effort
      }
    }
    return { context, analyser };
  }, []);

  const startOutputAnalysis = useCallback(() => {
    if (outputFrameRef.current) {
      window.cancelAnimationFrame(outputFrameRef.current);
      outputFrameRef.current = null;
    }
    const analyser = outputAnalyserRef.current;
    if (!analyser) {
      setAudioOutputLevel(0);
      return;
    }
    const data = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      if (!outputAnalyserRef.current) return;
      outputAnalyserRef.current.getByteFrequencyData(data);
      const avg = data.reduce((sum, item) => sum + item, 0) / (data.length || 1);
      setAudioOutputLevel(Math.max(0, Math.min(1, avg / 110)));
      outputFrameRef.current = window.requestAnimationFrame(tick);
    };
    tick();
  }, []);

  const hasActiveOutput = useCallback(() => Boolean(outputSourceRef.current || (currentAudioRef.current && !currentAudioRef.current.paused)), []);

  const queueVoicePreferenceSave = useCallback((saveOperation) => {
    const queued = voicePreferenceSaveQueueRef.current
      .catch(() => undefined)
      .then(saveOperation);
    voicePreferenceSaveQueueRef.current = queued.catch(() => undefined);
    return queued;
  }, []);

  const pushDetectedLanguageHint = useCallback((languageCode) => {
    if (!isAutoLanguageMode) return;
    const normalized = languageCodeForProvider(languageCode);
    if (!normalized || !VOICE_LANGUAGE_LABELS[normalized]) return;
    if (allowedAutoLanguages.length && !allowedAutoLanguages.includes(normalized)) return;
    // Detection must never mutate the customer's approved language allow-list.
    // The backend uses the detected language for this turn and answers in it.
  }, [allowedAutoLanguages, isAutoLanguageMode]);

  const clearSpeechQueue = useCallback(() => {
    ttsQueueGenerationRef.current += 1;
    ttsQueueRef.current = [];
    ttsQueueBusyRef.current = false;
    streamingReplyActiveRef.current = false;
    streamedChunkBoundaryRef.current = 0;
    finalStreamingReplyRef.current = false;
  }, []);

  useLayoutEffect(() => {
    const nextProfileKey = String(activeProfileUserId || "default");
    if (voiceSessionProfileRef.current === nextProfileKey) return;

    voiceOperationIdRef.current += 1;
    activeRealtimeTurnIdRef.current = null;
    voicePendingRef.current = false;
    voiceCaptureStartingRef.current = false;
    wakeWordEnabledRef.current = false;
    clearWakeWordBusy();
    clearWakeWindow();
    clearHandsFreeRestart();
    clearSpeechQueue();

    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
      } catch {
        // ignore recognition shutdown races during profile switches
      }
      recognitionRef.current = null;
    }
    manualRecognitionActiveRef.current = false;
    activeMicTurnRef.current = null;

    if (mediaRecorderRef.current) {
      const recorder = mediaRecorderRef.current;
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
      if (recorder.state !== "inactive") {
        try {
          recorder.stop();
        } catch {
          // ignore recorder shutdown races during profile switches
        }
      }
    }
    clearRecordingResources();

    if (currentAudioRef.current) {
      const audio = currentAudioRef.current;
      audio.onended = null;
      audio.onerror = null;
      audio.onloadedmetadata = null;
      audio.pause();
      audio.currentTime = 0;
      audio.removeAttribute?.("src");
      audio.load?.();
      currentAudioRef.current = null;
    }
    clearOutputMediaElementSource();
    clearOutputAnalysis();

    setVoicePending(false);
    setVoiceRecording(false);
    setHandsFreeMicEnabled(false);
    handsFreeMicEnabledRef.current = false;
    setWakeWordArmed(false);
    setVoiceInputMode("text");
    setVoiceCommand("");
    setWakeWordHeard("");
    setVoiceError("");
    setVoiceResult(null);
    setMemoryRecallResult(null);
    setVoiceDisplay((previous) => ({
      ...previous,
      response: "",
      transcript: "",
      intent: "",
      memoryNotice: "",
      wakeWordDetected: false,
    }));
    setVoiceStatus((previous) => {
      const nextStatus = createDefaultVoiceStatus();
      const policyBlockedDuringSwitch = Boolean(previous?.privacy?.policy_blocked);
      return {
        ...nextStatus,
        privacy: {
          ...nextStatus.privacy,
          policy_blocked: policyBlockedDuringSwitch,
          notice: policyBlockedDuringSwitch
            ? "Mikrofon ist im Trust Center ausgeschaltet."
            : nextStatus.privacy.notice,
        },
      };
    });

    voiceSessionProfileRef.current = nextProfileKey;
    setVoiceSessionId(getOrCreateProfileVoiceSessionId(nextProfileKey));
  }, [
    activeProfileUserId,
    clearHandsFreeRestart,
    clearOutputAnalysis,
    clearOutputMediaElementSource,
    clearRecordingResources,
    clearSpeechQueue,
    clearWakeWordBusy,
    clearWakeWindow,
  ]);

  const playAudioUrl = useCallback(async ({ audioUrl, audioBase64, audioFormat } = {}, { onDone, onError, locale } = {}) => {
    if (!audioUrl && !audioBase64) {
      await onDone?.();
      return;
    }
    clearOutputAnalysis();
    const playbackGeneration = outputPlaybackGenerationRef.current;
    const isCurrentPlayback = () => playbackGeneration === outputPlaybackGenerationRef.current;
    if (currentAudioRef.current) {
      const previousAudio = currentAudioRef.current;
      previousAudio.onended = null;
      previousAudio.onerror = null;
      previousAudio.onloadedmetadata = null;
      previousAudio.pause();
      previousAudio.currentTime = 0;
      if (String(previousAudio.src || "").startsWith("blob:")) {
        URL.revokeObjectURL(previousAudio.src);
      }
    }
    let objectUrl = null;
    let playbackSettled = false;
    let mediaElementFallbackId = null;
    const clearMediaElementFallback = () => {
      if (!mediaElementFallbackId) return;
      window.clearTimeout(mediaElementFallbackId);
      mediaElementFallbackId = null;
    };
    const revokeObjectUrl = () => {
      if (!objectUrl) return;
      if (outputObjectUrlRef.current === objectUrl) {
        outputObjectUrlRef.current = null;
      }
      URL.revokeObjectURL(objectUrl);
      objectUrl = null;
    };
    const settleDone = async () => {
      if (playbackSettled) return;
      playbackSettled = true;
      clearMediaElementFallback();
      revokeObjectUrl();
      clearOutputAnalysis();
      await onDone?.();
    };
    const settleError = async () => {
      if (playbackSettled) return;
      playbackSettled = true;
      clearMediaElementFallback();
      revokeObjectUrl();
      clearOutputAnalysis();
      await onError?.();
    };
    const resolvePlayableUrl = async () => {
      if (objectUrl) return objectUrl;
      if (audioBase64) {
        objectUrl = createObjectUrlFromBase64Audio(audioBase64, audioFormat);
        outputObjectUrlRef.current = objectUrl;
        return objectUrl;
      }
      const inlineAudio = parseAudioDataUrl(audioUrl);
      if (inlineAudio) {
        objectUrl = createObjectUrlFromBase64Audio(inlineAudio.base64, audioFormat, inlineAudio.mimeType);
        outputObjectUrlRef.current = objectUrl;
        return objectUrl;
      }
      return audioUrl;
    };
    try {
      const output = await ensureOutputContextUnlocked();
      if (output?.context && output?.analyser) {
        const resolvedUrl = await resolvePlayableUrl();
        const response = await fetch(resolvedUrl);
        const audioBuffer = await output.context.decodeAudioData((await response.arrayBuffer()).slice(0));
        if (!isCurrentPlayback()) {
          revokeObjectUrl();
          return;
        }
        const source = output.context.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(output.analyser);
        outputSourceRef.current = source;
        setLocalVoiceState(VOICE_STATES.SPEAKING, { privacy: { mic: "MIC_OFF", listening: false, notice: "AION spricht gerade." } });
        startOutputAnalysis();
        source.onended = async () => {
          if (outputSourceRef.current === source) outputSourceRef.current = null;
          await settleDone();
        };
        source.start(0);
        const playbackFallbackId = window.setTimeout(() => {
          if (!playbackSettled && outputSourceRef.current === source) {
            outputSourceRef.current = null;
            try {
              source.stop(0);
            } catch {
              // Safari may already have ended the buffer source.
            }
            void settleDone();
          }
        }, Math.max(WEB_AUDIO_END_GRACE_MS, Math.ceil((audioBuffer.duration || 0) * 1000) + WEB_AUDIO_END_GRACE_MS));
        source.addEventListener?.("ended", () => window.clearTimeout(playbackFallbackId), { once: true });
        return;
      }
    } catch {
      // fall back to media element playback below
    }
    if (!isCurrentPlayback()) {
      revokeObjectUrl();
      return;
    }
    try {
      const audio = currentAudioRef.current || new Audio();
      if (currentAudioRef.current !== audio) {
        clearOutputMediaElementSource();
      }
      audio.preload = "auto";
      audio.playsInline = true;
      audio.muted = false;
      audio.volume = 1;
      audio.setAttribute?.("playsinline", "true");
      audio.setAttribute?.("webkit-playsinline", "true");
      currentAudioRef.current = audio;
      let output = null;
      try {
        output = await ensureOutputContextUnlocked();
        if (output?.context && output?.analyser && !outputMediaElementSourceRef.current) {
          outputMediaElementSourceRef.current = output.context.createMediaElementSource(audio);
          outputMediaElementSourceRef.current.connect(output.analyser);
        }
      } catch {
        // Output analysis is optional. Plain HTMLAudio playback must still work
        // when Safari/Firefox blocks or closes the WebAudio context.
        clearOutputMediaElementSource();
      }
      const playableUrl = await resolvePlayableUrl();
      if (!isCurrentPlayback()) {
        revokeObjectUrl();
        return;
      }
      audio.src = playableUrl;
      audio.load?.();
      setLocalVoiceState(VOICE_STATES.SPEAKING, { privacy: { mic: "MIC_OFF", listening: false, notice: "AION spricht gerade." } });
      startOutputAnalysis();
      const cleanupMediaElementPlayback = () => {
        audio.onended = null;
        audio.onerror = null;
        audio.onloadedmetadata = null;
        try {
          audio.pause();
        } catch {
          // Safari can throw if the media element is already detached.
        }
        if (currentAudioRef.current === audio) currentAudioRef.current = null;
        clearOutputMediaElementSource();
      };
      const scheduleMediaElementFallback = () => {
        clearMediaElementFallback();
        const durationSeconds = Number(audio.duration);
        const delayMs = Number.isFinite(durationSeconds) && durationSeconds > 0
          ? Math.ceil(durationSeconds * 1000) + WEB_AUDIO_END_GRACE_MS
          : MEDIA_ELEMENT_UNKNOWN_DURATION_TIMEOUT_MS;
        mediaElementFallbackId = window.setTimeout(() => {
          mediaElementFallbackId = null;
          if (!playbackSettled && isCurrentPlayback()) {
            cleanupMediaElementPlayback();
            void settleDone();
          }
        }, delayMs);
      };
      audio.onended = async () => {
        cleanupMediaElementPlayback();
        await settleDone();
      };
      audio.onerror = async () => {
        cleanupMediaElementPlayback();
        await settleError();
      };
      audio.onloadedmetadata = scheduleMediaElementFallback;
      await audio.play();
      scheduleMediaElementFallback();
    } catch {
      if (!isCurrentPlayback()) {
        revokeObjectUrl();
        return;
      }
      if (!playbackSettled) {
        setVoiceError(normalizeVoiceError({ message: "TTS_FAILED" }, locale || selectedPrimaryLanguage));
        await settleError();
      }
    }
  }, [clearOutputAnalysis, clearOutputMediaElementSource, ensureOutputContextUnlocked, selectedPrimaryLanguage, setLocalVoiceState, startOutputAnalysis]);

  const flushTtsQueue = useCallback(async () => {
    if (ttsQueueBusyRef.current) return;
    const nextItem = ttsQueueRef.current.shift();
    if (!nextItem) {
      if (finalStreamingReplyRef.current) {
        await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current });
        clearSpeechQueue();
      }
      return;
    }
    ttsQueueBusyRef.current = true;
    const queueGeneration = ttsQueueGenerationRef.current;
    try {
      const speech = await speakVoiceText({ text: nextItem.text, language: nextItem.language, voice: VOICE_PROFILE_TO_TTS[assistantVoiceProfile], session_id: voiceSessionId, client_turn_id: activeRealtimeTurnIdRef.current || undefined }, token, nextItem.language);
      if (queueGeneration !== ttsQueueGenerationRef.current) return;
      applyVoiceSnapshot(speech);
      await playAudioUrl({ audioUrl: speech?.audio_url, audioBase64: speech?.audio_base64, audioFormat: speech?.audio_format }, {
        locale: nextItem.language,
        onDone: async () => {
          if (queueGeneration !== ttsQueueGenerationRef.current) return;
          ttsQueueBusyRef.current = false;
          await flushTtsQueue();
        },
        onError: async () => {
          if (queueGeneration !== ttsQueueGenerationRef.current) return;
          ttsQueueBusyRef.current = false;
          setVoiceError(normalizeVoiceError({ message: "TTS_FAILED" }, nextItem.language));
          await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current, preserveError: true });
          clearSpeechQueue();
        },
      });
    } catch {
      if (queueGeneration !== ttsQueueGenerationRef.current) return;
      ttsQueueBusyRef.current = false;
      setVoiceError(normalizeVoiceError({ message: "TTS_FAILED" }, nextItem.language));
      await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current, preserveError: true });
      clearSpeechQueue();
    }
  }, [applyVoiceSnapshot, assistantVoiceProfile, clearSpeechQueue, playAudioUrl, token, voiceSessionId]);

  const enqueueTtsChunk = useCallback((text, language, { isFinal = false } = {}) => {
    const normalizedText = String(text || "").trim();
    if (!normalizedText) {
      if (isFinal) {
        finalStreamingReplyRef.current = true;
        void flushTtsQueue();
      }
      return;
    }
    ttsQueueRef.current.push({ text: normalizedText, language: String(language || selectedPrimaryLanguage).includes("-") ? language : browserLocaleFor(language || selectedPrimaryLanguage) });
    if (isFinal) finalStreamingReplyRef.current = true;
    void flushTtsQueue();
  }, [flushTtsQueue, selectedPrimaryLanguage]);

  const transitionToReadyState = useCallback(async ({ sync = true, wakeMode = currentWakeMode, followUp = false, preserveError = false } = {}) => {
    const finishingOperationId = voiceOperationIdRef.current;
    const finishingTurnId = activeRealtimeTurnIdRef.current;
    const followUpActive = Boolean(followUp && wakeMode === WAKE_WORD_MODES.BROWSER && wakeWordEnabledRef.current && !voiceMuted);
    const handsFreeActive = handsFreeMicEnabledRef.current && !voiceMuted && wakeMode !== WAKE_WORD_MODES.BROWSER;
    const targetState = voiceMuted
      ? VOICE_STATES.MUTED
      : followUpActive
        ? VOICE_STATES.LISTENING
        : wakeMode === WAKE_WORD_MODES.BROWSER
          ? VOICE_STATES.WAKE
          : handsFreeActive
            ? VOICE_STATES.LISTENING
            : VOICE_STATES.IDLE;
    const privacy = targetState === VOICE_STATES.WAKE
      ? { mic: "MIC_ON", listening: true, notice: "Browser-Weckwort aktiv." }
      : targetState === VOICE_STATES.LISTENING
        ? { mic: "MIC_ON", listening: true, notice: followUpActive ? "Ich höre weiter zu …" : "Ich höre dauerhaft zu …" }
        : { mic: "MIC_OFF", listening: false, notice: voiceMuted ? "Mikrofon ist aus." : "Bereit für Text oder Sprache." };
    if (!preserveError) setVoiceError("");
    setVoiceRecording(false);
    setVoiceInputMode(wakeMode === WAKE_WORD_MODES.BROWSER ? "wake-word" : handsFreeActive ? "recording" : "text");
    clearSpeechQueue();
    clearWakeWindow();
    setLocalVoiceState(targetState, { wake_word_mode: wakeMode, privacy, partial_transcript: "" });
    if (sync) {
      await syncRuntimeState({
        voice_state: targetState,
        wake_word_mode: wakeMode,
        privacy,
        partial_transcript: "",
        metadata: finishingTurnId ? { client_turn_id: finishingTurnId } : undefined,
      }, { silent: true });
    }
    if (voiceOperationIdRef.current !== finishingOperationId) return;
    if (activeRealtimeTurnIdRef.current === finishingTurnId) {
      activeRealtimeTurnIdRef.current = null;
    }
    if (followUpActive) {
      wakeAwaitingCommandRef.current = true;
      wakeWindowTimerRef.current = window.setTimeout(() => {
        wakeWindowTimerRef.current = null;
        wakeAwaitingCommandRef.current = false;
        setWakeWordHeard("");
        const wakePrivacy = { mic: "MIC_ON", listening: true, notice: "Browser-Weckwort aktiv." };
        setLocalVoiceState(VOICE_STATES.WAKE, {
          wake_word_mode: WAKE_WORD_MODES.BROWSER,
          privacy: wakePrivacy,
          partial_transcript: "",
        });
        void syncRuntimeState({
          voice_state: VOICE_STATES.WAKE,
          wake_word_mode: WAKE_WORD_MODES.BROWSER,
          privacy: wakePrivacy,
          partial_transcript: "",
        }, { silent: true });
      }, FOLLOW_UP_WINDOW_MS);
    }
    if (handsFreeActive) {
      clearHandsFreeRestart();
      handsFreeRestartTimerRef.current = window.setTimeout(() => {
        handsFreeRestartTimerRef.current = null;
        if (!handsFreeMicEnabledRef.current || voiceMuted || policyBlocked || wakeWordEnabledRef.current || hasActiveOutput() || voicePendingRef.current) return;
        void startVoiceRecordingRef.current?.({ fromHandsFreeLoop: true });
      }, 260);
    }
  }, [clearHandsFreeRestart, clearSpeechQueue, clearWakeWindow, currentWakeMode, hasActiveOutput, policyBlocked, setLocalVoiceState, syncRuntimeState, voiceMuted]);
  transitionToReadyStateRef.current = transitionToReadyState;

  const stopCurrentSpeech = useCallback(async ({ notifyBackend = true, syncNextState = true, reason = "voice_interruption", nextState, nextWakeWordMode, nextPrivacy } = {}) => {
    const interruptionOperationId = voiceOperationIdRef.current;
    const audio = currentAudioRef.current;
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      audio.onloadedmetadata = null;
      audio.pause();
      audio.currentTime = 0;
      if (String(audio.src || "").startsWith("blob:")) {
        URL.revokeObjectURL(audio.src);
      }
      audio.removeAttribute?.("src");
      audio.load?.();
      currentAudioRef.current = null;
      clearOutputMediaElementSource();
    }
    clearOutputAnalysis();
    clearSpeechQueue();
    const resolvedWakeMode = nextState ? (nextWakeWordMode || currentWakeMode) : null;
    const resolvedPrivacy = nextState
      ? (nextPrivacy || (nextState === VOICE_STATES.LISTENING
        ? { mic: "MIC_ON", listening: true, notice: "Ich höre zu …" }
        : nextState === VOICE_STATES.WAKE
          ? { mic: "MIC_ON", listening: true, notice: "Browser-Weckwort aktiv." }
          : nextState === VOICE_STATES.MUTED
            ? { mic: "MIC_OFF", listening: false, notice: "Mikrofon ist aus." }
            : { mic: "MIC_OFF", listening: false, notice: "Bereit für Text oder Sprache." }))
      : null;
    if (nextState) {
      setLocalVoiceState(nextState, {
        wake_word_mode: resolvedWakeMode,
        privacy: resolvedPrivacy,
      });
    } else {
      setLocalVoiceState(VOICE_STATES.INTERRUPTED, { privacy: { mic: "MIC_ON", listening: false, notice: "Unterbrochen." } });
    }
    if (notifyBackend) {
      try {
        const snapshot = await interruptVoiceSession({ session_id: voiceSessionId, reason }, token);
        if (voiceOperationIdRef.current !== interruptionOperationId) return;
        if (!nextState) applyVoiceSnapshot(snapshot);
      } catch {
        // local interruption already happened
      }
    }
    if (voiceOperationIdRef.current !== interruptionOperationId) return;
    if (nextState && syncNextState) {
      await syncRuntimeState({
        voice_state: nextState,
        wake_word_mode: resolvedWakeMode,
        privacy: resolvedPrivacy,
      }, { silent: true });
    }
  }, [applyVoiceSnapshot, clearOutputAnalysis, clearOutputMediaElementSource, clearSpeechQueue, currentWakeMode, setLocalVoiceState, syncRuntimeState, token, voiceSessionId]);

  useEffect(() => {
    if (stateTimeoutRef.current) {
      window.clearTimeout(stateTimeoutRef.current);
      stateTimeoutRef.current = null;
    }
    const state = normalizeVoiceState(voiceStatus.voice_state);
    if (state === VOICE_STATES.TRANSCRIBING) {
      stateTimeoutRef.current = window.setTimeout(() => {
        void (async () => {
          voiceOperationIdRef.current += 1;
          activeRealtimeTurnIdRef.current = null;
          voicePendingRef.current = false;
          setVoicePending(false);
          setVoiceError(normalizeVoiceError({ status: 408, message: "Ich brauche gerade zu lange beim Verstehen." }, voiceStatus.detected_language || selectedPrimaryLanguage));
          setLocalVoiceState(VOICE_STATES.ERROR, { error_code: "TRANSCRIBING_TIMEOUT" });
          await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current, preserveError: true });
        })();
      }, TRANSCRIBING_TIMEOUT_MS);
    } else if (state === VOICE_STATES.THINKING) {
      stateTimeoutRef.current = window.setTimeout(() => {
        void (async () => {
          voiceOperationIdRef.current += 1;
          activeRealtimeTurnIdRef.current = null;
          voicePendingRef.current = false;
          setVoicePending(false);
          setVoiceError(normalizeVoiceError({ status: 408, message: "AION braucht gerade zu lange." }, voiceStatus.detected_language || selectedPrimaryLanguage));
          setLocalVoiceState(VOICE_STATES.ERROR, { error_code: "VOICE_TIMEOUT" });
          await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current, preserveError: true });
        })();
      }, THINKING_TIMEOUT_MS);
    } else if (state === VOICE_STATES.RESPONDING) {
      stateTimeoutRef.current = window.setTimeout(() => {
        void (async () => {
          voiceOperationIdRef.current += 1;
          activeRealtimeTurnIdRef.current = null;
          voicePendingRef.current = false;
          setVoicePending(false);
          setVoiceError(normalizeVoiceError({ status: 408, message: "AION braucht gerade zu lange." }, voiceStatus.detected_language || selectedPrimaryLanguage));
          setLocalVoiceState(VOICE_STATES.ERROR, { error_code: "VOICE_TIMEOUT" });
          await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current, preserveError: true });
        })();
      }, RESPONDING_TIMEOUT_MS);
    } else if (state === VOICE_STATES.SPEAKING) {
      stateTimeoutRef.current = window.setTimeout(() => {
        void (async () => {
          await stopCurrentSpeech({
            notifyBackend: true,
            syncNextState: false,
            reason: "speaking_timeout",
          });
          await transitionToReadyStateRef.current?.({
            sync: true,
            followUp: wakeWordEnabledRef.current,
          });
        })();
      }, SPEAKING_TIMEOUT_MS);
    }
    return () => {
      if (stateTimeoutRef.current) {
        window.clearTimeout(stateTimeoutRef.current);
        stateTimeoutRef.current = null;
      }
    };
  }, [selectedPrimaryLanguage, setLocalVoiceState, stopCurrentSpeech, voiceMuted, voiceStatus.detected_language, voiceStatus.voice_state, wakeWordArmed]);

  useEffect(() => {
    let cancelled = false;
    const bootVoice = async () => {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const restrictedWakeWordBrowser = isCurrentIOSLikeBrowser();
      setWakeWordSupported(Boolean(SpeechRecognition) && !restrictedWakeWordBrowser);
      try {
        const [statusResult, recall] = await Promise.all([
          getVoiceStatus(token, voiceSessionId),
          recallMemory({ session_id: voiceSessionId, limit: 8, profile_user_id: activeProfileUserId }, token),
        ]);
        if (cancelled) return;
        const startupState = normalizeVoiceState(statusResult?.voice_state);
        const startupLooksActive = [
          VOICE_STATES.WAKE,
          VOICE_STATES.LISTENING,
          VOICE_STATES.SPEECH_DETECTED,
          VOICE_STATES.TRANSCRIBING,
          VOICE_STATES.THINKING,
          VOICE_STATES.EXECUTING,
          VOICE_STATES.RESPONDING,
          VOICE_STATES.SPEAKING,
          VOICE_STATES.INTERRUPTED,
        ].includes(startupState);
        const startupMuted = Boolean(statusResult?.privacy?.policy_blocked) || startupState === VOICE_STATES.MUTED;
        let bootSnapshot = { ...statusResult, wake_word_ready: Boolean(SpeechRecognition) && !restrictedWakeWordBrowser };
        if (startupLooksActive) {
          const resetSnapshot = await syncVoiceState({
            session_id: voiceSessionId,
            voice_state: startupMuted ? VOICE_STATES.MUTED : VOICE_STATES.IDLE,
            wake_word_mode: WAKE_WORD_MODES.DISABLED,
            partial_transcript: "",
            privacy: startupMuted
              ? { mic: "MIC_OFF", listening: false, notice: "Mikrofon ist aus." }
              : { mic: "MIC_OFF", listening: false, notice: "Bereit für Text oder Sprache." },
            metadata: { wake_word_provider: WAKE_WORD_MODES.DISABLED },
          }, token);
          if (cancelled) return;
          bootSnapshot = { ...bootSnapshot, ...resetSnapshot, wake_word_ready: Boolean(SpeechRecognition) && !restrictedWakeWordBrowser };
        }
        if (cancelled) return;
        applyVoiceSnapshot(bootSnapshot);
        setVoiceMuted(startupMuted);
        setWakeWordArmed(false);
        setMemoryRecallResult(recall);
      } catch {
        if (!cancelled) {
          applyVoiceSnapshot({ voice_ready: false, stt_ready: false, tts_ready: false, error_code: "VOICE_STATUS_UNAVAILABLE", display_state: "Voice nicht verbunden" });
        }
      }
    };
    void bootVoice();
    return () => {
      cancelled = true;
    };
  }, [activeProfileUserId, applyVoiceSnapshot, token, voiceSessionId]);

  useEffect(() => {
    if (!trustPrivacyKey) return undefined;
    let cancelled = false;
    const syncTrustPrivacy = async () => {
      try {
        const statusResult = await getVoiceStatus(token, voiceSessionId);
        if (cancelled) return;
        applyVoiceSnapshot({
          voice_ready: statusResult?.voice_ready,
          stt_ready: statusResult?.stt_ready,
          tts_ready: statusResult?.tts_ready,
          wake_word_ready: statusResult?.wake_word_ready,
          supported_wake_word_modes: statusResult?.supported_wake_word_modes,
          stt_provider: statusResult?.stt_provider,
          tts_provider: statusResult?.tts_provider,
          privacy: statusResult?.privacy,
        });
        if (statusResult?.privacy?.policy_blocked) {
          setVoiceMuted(true);
        }
      } catch {
        // trust-center refresh should not reset the active voice loop
      }
    };
    void syncTrustPrivacy();
    return () => {
      cancelled = true;
    };
  }, [applyVoiceSnapshot, token, trustPrivacyKey, voiceSessionId]);

  useEffect(() => {
    if (policyBlocked) {
      voiceOperationIdRef.current += 1;
      activeRealtimeTurnIdRef.current = null;
      voicePendingRef.current = false;
      setVoicePending(false);
      setVoiceMuted(true);
      setHandsFreeMicEnabled(false);
      handsFreeMicEnabledRef.current = false;
      clearHandsFreeRestart();
      clearWakeWindow();
      wakeWordEnabledRef.current = false;
      setWakeWordArmed(false);
      voiceCaptureStartingRef.current = false;
      activeMicTurnRef.current = null;

      if (mediaRecorderRef.current) {
        const recorder = mediaRecorderRef.current;
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.onerror = null;
        if (recorder.state !== "inactive") {
          try {
            recorder.stop();
          } catch {
            // ignore recorder shutdown races after privacy policy changes
          }
        }
      }

      if (recognitionRef.current) {
        try {
          recognitionRef.current.onresult = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.onend = null;
          recognitionRef.current.stop();
        } catch {
          // ignore browser recognition shutdown races after privacy policy changes
        }
        recognitionRef.current = null;
      }

      manualRecognitionActiveRef.current = false;
      clearRecordingResources();
      setVoiceRecording(false);
      setVoiceInputMode("text");
      setLocalVoiceState(VOICE_STATES.MUTED, {
        wake_word_mode: WAKE_WORD_MODES.DISABLED,
        partial_transcript: "",
        privacy: { mic: "MIC_OFF", listening: false, notice: "Mikrofon ist im Trust Center ausgeschaltet." },
      });
    }
  }, [clearHandsFreeRestart, clearRecordingResources, clearWakeWindow, policyBlocked, setLocalVoiceState]);

  const playVoiceReply = useCallback(async (text, language, speechPayload = null, { operationId = null } = {}) => {
    const isCurrentOperation = () => operationId === null || operationId === voiceOperationIdRef.current;
    if (!isCurrentOperation()) return;
    if (!text?.trim()) {
      await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current });
      return;
    }
    try {
      const speechLocale = String(language || "").includes("-") ? language : browserLocaleFor(language || selectedPrimaryLanguage);
      const speech = speechPayload?.audioBase64 || speechPayload?.audioUrl
        ? {
          audio_url: speechPayload?.audioUrl || null,
          audio_base64: speechPayload?.audioBase64 || null,
          audio_format: speechPayload?.audioFormat || "mp3",
          provider: speechPayload?.provider || "openai-tts",
        }
        : await speakVoiceText({ text, language: speechLocale, voice: VOICE_PROFILE_TO_TTS[assistantVoiceProfile], session_id: voiceSessionId, client_turn_id: activeRealtimeTurnIdRef.current || undefined }, token, selectedPrimaryLanguage);
      if (!isCurrentOperation()) return;
      applyVoiceSnapshot(speech);
      if (speech?.audio_url || speech?.audio_base64) {
        await playAudioUrl({ audioUrl: speech.audio_url, audioBase64: speech.audio_base64, audioFormat: speech.audio_format }, {
          locale: speechLocale,
          onDone: async () => {
            if (!isCurrentOperation()) return;
            await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current });
          },
          onError: async () => {
            if (!isCurrentOperation()) return;
            setVoiceError(normalizeVoiceError({ message: "TTS_FAILED" }, speechLocale));
            setLocalVoiceState(VOICE_STATES.RESPONDING, { fallback_used: true, error_code: "TTS_FAILED" });
            await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current, preserveError: true });
          },
        });
      } else {
        await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current });
      }
    } catch {
      if (!isCurrentOperation()) return;
      setVoiceError(normalizeVoiceError({ message: "TTS_FAILED" }, language || selectedPrimaryLanguage));
      setLocalVoiceState(VOICE_STATES.ERROR, { error_code: "TTS_FAILED" });
      await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current, preserveError: true });
    }
  }, [applyVoiceSnapshot, assistantVoiceProfile, playAudioUrl, selectedPrimaryLanguage, setLocalVoiceState, token, voiceSessionId]);

  const handleRealtimeVoiceEvent = useCallback((message) => {
    const eventType = message?.data?.type || message?.type || message?.event_type;
    const payload = message?.data?.payload || message?.payload || message?.data || {};
    if (!eventType) return;
    const payloadSessionId = String(payload?.session_id || "").trim();
    const sessionScopedRealtimeEvent = ["voice_state_changed", "voice_interrupted", "voice_transcribed", "voice_response_partial", "voice_response_generated", "voice_audio_ready"].includes(eventType);
    if (sessionScopedRealtimeEvent && (!payloadSessionId || payloadSessionId !== voiceSessionId)) return;
    const turnScopedRealtimeEvent = ["voice_transcribed", "voice_response_partial", "voice_response_generated", "voice_audio_ready"].includes(eventType);
    if (turnScopedRealtimeEvent) {
      const payloadTurnId = String(payload?.client_turn_id || "").trim();
      const activeTurnId = activeRealtimeTurnIdRef.current;
      if (!payloadTurnId || !activeTurnId || payloadTurnId !== activeTurnId) return;
    }
    if (eventType === "voice_state_changed") {
      const payloadTurnId = String(payload?.client_turn_id || "").trim();
      const activeTurnId = activeRealtimeTurnIdRef.current;
      if (payloadTurnId && payloadTurnId !== activeTurnId) return;
    }
    if (eventType === "voice_interrupted") {
      // The local interruption path already updates state. A delayed websocket
      // interrupt from an older turn must not overwrite a newer in-flight turn.
      if (voicePendingRef.current) return;
      applyVoiceSnapshot(payload);
      return;
    }
    if (eventType === "voice_state_changed" || eventType === "voice_audio_ready") {
      applyVoiceSnapshot(payload);
      return;
    }
    if (eventType === "voice_transcribed") {
      pushDetectedLanguageHint(payload?.language);
      setVoiceDisplay((prev) => ({ ...prev, transcript: payload?.text || prev.transcript, language: payload?.language || prev.language }));
      return;
    }
    if (eventType === "voice_response_partial") {
      const language = payload?.language || selectedPrimaryLanguage;
      streamingReplyActiveRef.current = true;
      streamedLanguageRef.current = language;
      pushDetectedLanguageHint(language);
      applyVoiceSnapshot({
        voice_state: VOICE_STATES.RESPONDING,
        display_state: VOICE_STATE_DISPLAY[VOICE_STATES.RESPONDING],
        response_streaming: true,
        tts_chunking_active: true,
        route_classification: payload?.route_classification,
        latency: payload?.latency || {},
      });
      setVoiceDisplay((prev) => ({
        ...prev,
        response: payload?.response || prev.response,
        language,
      }));
      if (payload?.ready_tts_chunk && payload?.chunk_boundary > streamedChunkBoundaryRef.current) {
        streamedChunkBoundaryRef.current = payload.chunk_boundary;
        enqueueTtsChunk(payload.ready_tts_chunk, language, { isFinal: Boolean(payload?.is_final) });
      } else if (payload?.is_final) {
        finalStreamingReplyRef.current = true;
        void flushTtsQueue();
      }
      return;
    }
    if (eventType === "voice_response_generated") {
      const language = payload?.language || selectedPrimaryLanguage;
      pushDetectedLanguageHint(language);
      setVoiceDisplay((prev) => ({
        ...prev,
        response: payload?.response || prev.response,
        language,
      }));
    }
  }, [applyVoiceSnapshot, enqueueTtsChunk, flushTtsQueue, pushDetectedLanguageHint, selectedPrimaryLanguage, voiceSessionId]);

  const runConversation = useCallback(async (text, options = {}) => {
    const { source = "text", wakeWordDetected = false, latency = {}, roomId, detectedLanguage } = options;
    if (!text?.trim()) return null;
    const operationId = voiceOperationIdRef.current + 1;
    voiceOperationIdRef.current = operationId;
    const clientTurnId = `${voiceSessionId}:${Date.now()}:${operationId}`;
    activeRealtimeTurnIdRef.current = clientTurnId;
    if (isStopCommand(text)) {
      await stopCurrentSpeech({
        notifyBackend: true,
        reason: source === "wake-word" ? "voice_stop_command_wake_word" : "voice_stop_command",
        nextState: wakeWordArmed ? VOICE_STATES.WAKE : (voiceMuted ? VOICE_STATES.MUTED : VOICE_STATES.IDLE),
        nextWakeWordMode: wakeWordArmed ? WAKE_WORD_MODES.BROWSER : WAKE_WORD_MODES.DISABLED,
      });
      setVoiceDisplay({ response: "", language: selectedPrimaryLanguage, intent: "voice_stop", wakeWordDetected, transcript: text });
      return { intent: "voice_stop", transcript: text };
    }

    // Every turn owns a fresh streaming/TTS generation. Reset stale chunk
    // boundaries even when the previous turn lost its final realtime event.
    clearSpeechQueue();
    if (hasActiveOutput()) {
      await stopCurrentSpeech({ notifyBackend: false, syncNextState: false, reason: "barge_in_new_turn", nextState: VOICE_STATES.LISTENING, nextWakeWordMode: WAKE_WORD_MODES.DISABLED, nextPrivacy: { mic: "MIC_ON", listening: true, notice: "Ich höre zu …" } });
    }
    voicePendingRef.current = true;
    setVoicePending(true);
    setVoiceError("");
    setLocalVoiceState(VOICE_STATES.THINKING, {
      wake_word_mode: source === "wake-word" || wakeWordArmed ? WAKE_WORD_MODES.BROWSER : WAKE_WORD_MODES.DISABLED,
      partial_transcript: "",
      privacy: { mic: "MIC_OFF", listening: false, notice: "Ich denke nach …" },
    });
    try {
      const spokenTurn = ["wake-word", "audio", "recording", "speech", "voice"].includes(source);
      const requestPayload = {
        text,
        session_id: voiceSessionId,
        profile_user_id: activeProfileUserId,
        room_id: roomId || currentRoomContext.room_id || undefined,
        tts_voice: VOICE_PROFILE_TO_TTS[assistantVoiceProfile],
        metadata: {
          client_turn_id: clientTurnId,
          source,
          selected_language: isAutoLanguageMode ? undefined : selectedProviderLanguage,
          selected_language_locale: isAutoLanguageMode ? undefined : selectedPrimaryLocale,
          selected_languages: selectedVoiceLanguages,
          language_mode: isAutoLanguageMode ? "auto" : "locked",
          ...(detectedLanguage ? { detected_language: detectedLanguage } : {}),
          wake_word_mode: source === "wake-word" || wakeWordArmed ? WAKE_WORD_MODES.BROWSER : WAKE_WORD_MODES.DISABLED,
          privacy: { mic: "MIC_OFF", listening: false, notice: "Ich denke nach …" },
          latency,
          room_context: currentRoomContext,
        },
      };
      const result = spokenTurn
        ? await sendVoiceCommand({
          ...requestPayload,
          wake_word_required: false,
          tts_enabled: true,
        }, token, selectedPrimaryLanguage)
        : await startVoiceConversation(requestPayload, token, selectedPrimaryLanguage);
      if (operationId !== voiceOperationIdRef.current) return null;
      setVoiceResult(result);
      if (result?.session_id) {
        setVoiceSessionId(result.session_id);
        window.localStorage.setItem(voiceSessionStorageKey(activeProfileUserId), result.session_id);
      }
      applyVoiceSnapshot(result);
      pushDetectedLanguageHint(result?.detected_language || result?.language);
      setVoiceDisplay({
        response: result?.response || "",
        language: result?.language || selectedPrimaryLanguage,
        intent: result?.intent || "",
        wakeWordDetected,
        transcript: result?.transcript || text,
        memoryNotice: buildMemoryLearnedNotice(result),
      });
      if (result?.intent === "memory_save") {
        toast.success("Neu gelernt", {
          description: buildMemoryLearnedNotice(result),
        });
      }
      voicePendingRef.current = false;
      setVoicePending(false);
      void recallMemory({ session_id: result?.session_id || voiceSessionId, limit: 8, profile_user_id: activeProfileUserId }, token)
        .then((recall) => {
          if (operationId === voiceOperationIdRef.current) setMemoryRecallResult(recall);
        })
        .catch(() => undefined);
      if (result?.intent !== "voice_stop" && !(streamingReplyActiveRef.current && (result?.response_streaming || result?.tts_chunking_active))) {
        void playVoiceReply(result?.response || "", result?.language || selectedPrimaryLocale, {
          audioBase64: result?.tts_audio_base64 || null,
          audioFormat: result?.tts_format || "mp3",
          provider: result?.tts_provider || "openai-tts",
        }, { operationId });
      } else if (streamingReplyActiveRef.current && (result?.response_streaming || result?.tts_chunking_active)) {
        finalStreamingReplyRef.current = true;
        void flushTtsQueue();
      }
      void onRefresh?.();
      return result;
    } catch (error) {
      if (operationId !== voiceOperationIdRef.current) return null;
      setVoiceError(normalizeVoiceError(error, selectedPrimaryLanguage));
      setLocalVoiceState(VOICE_STATES.ERROR, { error_code: "NETWORK_ERROR" });
      throw error;
    } finally {
      if (operationId === voiceOperationIdRef.current) {
        voicePendingRef.current = false;
        setVoicePending((current) => (current ? false : current));
      }
    }
  }, [activeProfileUserId, applyVoiceSnapshot, assistantVoiceProfile, clearSpeechQueue, currentRoomContext, flushTtsQueue, hasActiveOutput, isAutoLanguageMode, onRefresh, playVoiceReply, pushDetectedLanguageHint, selectedPrimaryLanguage, selectedPrimaryLocale, selectedProviderLanguage, selectedVoiceLanguages, setLocalVoiceState, stopCurrentSpeech, token, voiceMuted, voiceSessionId, wakeWordArmed]);

  const handleVoiceSubmit = async () => {
    await ensureOutputContextUnlocked({ prime: true }).catch(() => null);
    const result = await runConversation(voiceCommand, { source: "text" }).catch(() => null);
    if (result) {
      setVoiceCommand("");
    }
  };

  const processRecognizedTranscript = useCallback(async (transcriptText, options = {}) => {
    const normalizedTranscript = String(transcriptText || "").trim();
    const transcriptLanguage = languageCodeForProvider(options.detectedLanguage || selectedPrimaryLanguage) || selectedPrimaryLanguage;
    if (!normalizedTranscript) {
      setVoiceError(voiceClientErrorCopy(selectedPrimaryLanguage, "audio"));
      setVoiceCommand("");
      setLocalVoiceState(VOICE_STATES.ERROR, { error_code: "NO_AUDIO" });
      await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current, preserveError: true });
      return null;
    }
    const transcriptCompatibleWithLockedLanguage = isTranscriptCompatibleWithLockedLanguage(
      normalizedTranscript,
      selectedPrimaryLanguage,
    );
    const lockedLanguageMismatch = !isAutoLanguageMode
      && transcriptLanguage
      && languageCodeForProvider(transcriptLanguage) !== languageCodeForProvider(selectedPrimaryLanguage)
      && !transcriptCompatibleWithLockedLanguage;
    if (!isAutoLanguageMode && (lockedLanguageMismatch || !transcriptCompatibleWithLockedLanguage)) {
      voicePendingRef.current = false;
      setVoicePending(false);
      setVoiceError(transcriptMismatchMessage(selectedPrimaryLanguage));
      setVoiceDisplay((prev) => ({
        ...prev,
        transcript: "",
        language: selectedPrimaryLanguage,
      }));
      setVoiceCommand("");
      setLocalVoiceState(VOICE_STATES.ERROR, { error_code: "TRANSCRIPT_LANGUAGE_MISMATCH" });
      await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current, preserveError: true });
      return null;
    }
    setVoiceDisplay((prev) => ({
      ...prev,
      transcript: normalizedTranscript,
      language: transcriptLanguage,
      wakeWordDetected: Boolean(options.wakeWordDetected),
    }));
    setVoiceCommand("");
    pushDetectedLanguageHint(transcriptLanguage);
    applyVoiceSnapshot({ partial_transcript: normalizedTranscript, detected_language: transcriptLanguage, latency: options.latency || {} });
    const result = await runConversation(normalizedTranscript, {
      source: options.source || "audio",
      wakeWordDetected: Boolean(options.wakeWordDetected),
      latency: options.latency || {},
      detectedLanguage: transcriptLanguage,
      roomId: options.roomId,
    });
    if (result) {
      setVoiceCommand("");
    }
    return result;
  }, [applyVoiceSnapshot, isAutoLanguageMode, pushDetectedLanguageHint, runConversation, selectedPrimaryLanguage, setLocalVoiceState]);

  const processAudioConversation = useCallback(async (audioBase64, audioFormat, options = {}) => {
    const operationId = voiceOperationIdRef.current + 1;
    voiceOperationIdRef.current = operationId;
    const clientTurnId = `${voiceSessionId}:${Date.now()}:${operationId}:stt`;
    activeRealtimeTurnIdRef.current = clientTurnId;
    const sttStartedAt = performance.now();
    voicePendingRef.current = true;
    setVoicePending(true);
    setVoiceError("");
    setVoiceDisplay((prev) => ({
      ...prev,
      response: "",
      intent: "",
      wakeWordDetected: Boolean(options.wakeWordDetected),
    }));
    setLocalVoiceState(VOICE_STATES.TRANSCRIBING, {
      wake_word_mode: options.wakeWordMode || WAKE_WORD_MODES.DISABLED,
      privacy: { mic: "MIC_OFF", listening: false, notice: "Ich verstehe …" },
    });
    await syncRuntimeState({
      voice_state: VOICE_STATES.TRANSCRIBING,
      wake_word_mode: options.wakeWordMode || WAKE_WORD_MODES.DISABLED,
      privacy: { mic: "MIC_OFF", listening: false, notice: "Ich verstehe …" },
    }, { silent: true });
    const transcript = await transcribeVoiceAudio({
      audio_base64: audioBase64,
      audio_format: audioFormat,
      session_id: voiceSessionId,
      client_turn_id: clientTurnId,
      language: isAutoLanguageMode ? undefined : selectedPrimaryLocale,
    }, token, selectedPrimaryLanguage);
    if (operationId !== voiceOperationIdRef.current) return null;
    const sttDurationMs = Math.round(performance.now() - sttStartedAt);
    const transcriptText = (transcript?.text || "").trim();
    const providerDetectedLanguage = languageCodeForProvider(transcript?.language);
    const transcriptLanguage = isAutoLanguageMode
      ? detectTranscriptLanguage(transcriptText, providerDetectedLanguage || "en")
      : (
          isTranscriptCompatibleWithLockedLanguage(transcriptText, selectedPrimaryLanguage)
            ? selectedPrimaryLanguage
            : (providerDetectedLanguage || detectTranscriptLanguage(transcriptText, selectedPrimaryLanguage))
        );
    if (!transcriptText) {
      setVoiceError(voiceClientErrorCopy(selectedPrimaryLanguage, "speech"));
      setLocalVoiceState(VOICE_STATES.ERROR, {
        wake_word_mode: options.wakeWordMode || WAKE_WORD_MODES.DISABLED,
        error_code: "STT_FAILED",
        privacy: { mic: "MIC_OFF", listening: false, notice: "Keine klare Transkription erkannt." },
      });
      await syncRuntimeState({
        voice_state: VOICE_STATES.ERROR,
        wake_word_mode: options.wakeWordMode || WAKE_WORD_MODES.DISABLED,
        error_code: "STT_FAILED",
        privacy: { mic: "MIC_OFF", listening: false, notice: "Keine klare Transkription erkannt." },
      }, { silent: true });
      voicePendingRef.current = false;
      setVoicePending(false);
      setVoiceCommand("");
      await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current, preserveError: true });
      return null;
    }
    return processRecognizedTranscript(transcriptText, {
      source: options.source || "audio",
      wakeWordDetected: Boolean(options.wakeWordDetected),
      latency: { stt_duration_ms: sttDurationMs, wake_to_listen_ms: options.wakeToListenMs || undefined },
      detectedLanguage: transcriptLanguage,
      roomId: options.roomId,
    });
  }, [isAutoLanguageMode, processRecognizedTranscript, selectedPrimaryLanguage, selectedPrimaryLocale, setLocalVoiceState, syncRuntimeState, token, voiceSessionId]);

  const stopWakeWordMode = useCallback(async ({ toMuted = false, preserveError = false } = {}) => {
    wakeWordEnabledRef.current = false;
    clearWakeWordBusy();
    clearWakeWindow();
    setWakeWordArmed(false);
    setVoiceInputMode("text");
    setWakeWordHeard("");
    const hadManualRecognition = manualRecognitionActiveRef.current;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
      } catch {
        // ignore stop races
      }
      recognitionRef.current = null;
    }
    manualRecognitionActiveRef.current = false;
    activeMicTurnRef.current = null;
    voiceCaptureStartingRef.current = false;
    if (hadManualRecognition) {
      setVoiceRecording(false);
    }
    if (toMuted) {
      setLocalVoiceState(VOICE_STATES.MUTED, {
        wake_word_mode: WAKE_WORD_MODES.DISABLED,
        partial_transcript: "",
        privacy: { mic: "MIC_OFF", listening: false, notice: "Mikrofon ist aus." },
      });
      await syncRuntimeState({
        voice_state: VOICE_STATES.MUTED,
        wake_word_mode: WAKE_WORD_MODES.DISABLED,
        partial_transcript: "",
        privacy: { mic: "MIC_OFF", listening: false, notice: "Mikrofon ist aus." },
      }, { silent: true });
      return;
    }
    await transitionToReadyState({ sync: true, wakeMode: WAKE_WORD_MODES.DISABLED, preserveError });
  }, [clearWakeWordBusy, clearWakeWindow, setLocalVoiceState, syncRuntimeState, transitionToReadyState]);

  const scheduleWakeWindow = useCallback(() => {
    clearWakeWindow();
    wakeAwaitingCommandRef.current = true;
    wakeWindowTimerRef.current = window.setTimeout(async () => {
      wakeAwaitingCommandRef.current = false;
      setWakeWordHeard("");
      const privacy = { mic: "MIC_ON", listening: true, notice: "Browser-Weckwort aktiv." };
      setLocalVoiceState(VOICE_STATES.WAKE, {
        wake_word_mode: WAKE_WORD_MODES.BROWSER,
        privacy,
        partial_transcript: "",
      });
      await syncRuntimeState({
        voice_state: VOICE_STATES.WAKE,
        wake_word_mode: WAKE_WORD_MODES.BROWSER,
        privacy,
        partial_transcript: "",
      }, { silent: true });
    }, WAKE_WINDOW_MS);
  }, [clearWakeWindow, setLocalVoiceState, syncRuntimeState]);

  const startWakeWordMode = async () => {
    if (policyBlocked) {
      setVoiceError(voiceClientErrorCopy(selectedPrimaryLanguage, "mic"));
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (voiceMutedRef.current) {
      setVoiceError(voiceClientErrorCopy(selectedPrimaryLanguage, "mic"));
      setLocalVoiceState(VOICE_STATES.MUTED, { privacy: { mic: "MIC_OFF", listening: false, notice: "Mikrofon ist aus." } });
      return;
    }
    if (isCurrentIOSLikeBrowser()) {
      setVoiceError(voiceClientErrorCopy(selectedPrimaryLanguage, "speech"));
      return;
    }
    if (!SpeechRecognition) {
      setVoiceError(voiceClientErrorCopy(selectedPrimaryLanguage, "speech"));
      return;
    }
    if (hasActiveOutput()) {
      await stopCurrentSpeech({ notifyBackend: false, syncNextState: false, reason: "barge_in_wake_word", nextState: VOICE_STATES.WAKE, nextWakeWordMode: WAKE_WORD_MODES.BROWSER });
    }
    try {
      wakeWordBusyRef.current = false;
      setVoiceError("");
      setWakeWordHeard("");
      setVoiceInputMode("wake-word");
      wakeWordEnabledRef.current = true;
      setWakeWordArmed(true);
      const recognition = new SpeechRecognition();
      let wakeRecognitionSegments = [];
      recognition.lang = browserLocaleFor(selectedPrimaryLanguage);
      recognition.interimResults = true;
      recognition.continuous = true;
      recognition.maxAlternatives = 1;
      lastWakeDetectedAtRef.current = 0;

      setLocalVoiceState(VOICE_STATES.WAKE, {
        wake_word_mode: WAKE_WORD_MODES.BROWSER,
        wake_word_provider: WAKE_WORD_MODES.BROWSER,
        privacy: { mic: "MIC_ON", listening: true, notice: "Browser-Weckwort aktiv." },
      });
      await syncRuntimeState({
        voice_state: VOICE_STATES.WAKE,
        wake_word_mode: WAKE_WORD_MODES.BROWSER,
        privacy: { mic: "MIC_ON", listening: true, notice: "Browser-Weckwort aktiv." },
      }, { silent: true });

      recognition.onresult = async (event) => {
        const mergedResults = mergeSpeechRecognitionResults(wakeRecognitionSegments, event);
        wakeRecognitionSegments = mergedResults.segments;
        const resultIndex = Number.isInteger(event.resultIndex) ? Math.max(0, event.resultIndex) : 0;
        const firstInterimIndex = wakeRecognitionSegments.findIndex((segment) => segment?.transcript && !segment.isFinal);
        const activeStartIndex = firstInterimIndex >= 0 ? Math.min(resultIndex, firstInterimIndex) : resultIndex;
        const activeSegments = wakeRecognitionSegments.slice(activeStartIndex).filter((segment) => Boolean(segment?.transcript));
        const latest = activeSegments.map((segment) => segment.transcript).join(" ").replace(/\s+/g, " ").trim();
        const activeHasFinal = activeSegments.some((segment) => segment.isFinal);
        const activeHasInterim = activeSegments.some((segment) => !segment.isFinal);
        const activeComplete = Boolean(latest) && activeHasFinal && !activeHasInterim;
        if (!latest) return;

        const normalized = latest.toLowerCase();
        const speakingNow = hasActiveOutput();
        applyVoiceSnapshot({ partial_transcript: latest });
        if (wakeWordBusyRef.current) return;

        if (speakingNow) {
          // Keep browser recognition armed during TTS, but ignore non-wake speech so
          // AION cannot mistake its own speaker output for a user barge-in.
          if (!normalized.includes(WAKE_WORD)) return;
          await stopCurrentSpeech({
            notifyBackend: false,
            syncNextState: false,
            reason: "barge_in_wake_word_detected",
            nextState: VOICE_STATES.WAKE,
            nextWakeWordMode: WAKE_WORD_MODES.BROWSER,
            nextPrivacy: { mic: "MIC_ON", listening: true, notice: "Ich höre zu …" },
          });
        }

        if (!wakeAwaitingCommandRef.current && normalized.includes(WAKE_WORD)) {
          const afterWake = cleanWakeWordCommand(latest);
          lastWakeDetectedAtRef.current = performance.now();
          setWakeWordHeard(latest);
          setLocalVoiceState(afterWake ? VOICE_STATES.SPEECH_DETECTED : VOICE_STATES.WAKE, {
            wake_word_mode: WAKE_WORD_MODES.BROWSER,
            partial_transcript: latest,
            privacy: { mic: "MIC_ON", listening: true, notice: afterWake ? "Ich höre dich …" : "Sag einfach weiter …" },
          });
          await syncRuntimeState({
            voice_state: afterWake ? VOICE_STATES.SPEECH_DETECTED : VOICE_STATES.WAKE,
            wake_word_mode: WAKE_WORD_MODES.BROWSER,
            partial_transcript: latest,
            privacy: { mic: "MIC_ON", listening: true, notice: afterWake ? "Ich höre dich …" : "Sag einfach weiter …" },
          }, { silent: true });
          if (!afterWake) {
            scheduleWakeWindow();
            return;
          }
          if (!activeComplete) return;
          wakeWordBusyRef.current = true;
          clearWakeWindow();
          setVoiceCommand(afterWake);
          try {
            await runConversation(afterWake, { source: "wake-word", wakeWordDetected: true, latency: { wake_to_listen_ms: 0 } });
          } finally {
            scheduleWakeWordBusyRelease();
          }
          return;
        }

        if (wakeAwaitingCommandRef.current) {
          const command = cleanWakeWordCommand(latest);
          if (!command || !activeComplete) return;
          wakeWordBusyRef.current = true;
          clearWakeWindow();
          setVoiceCommand(command);
          setWakeWordHeard(command);
          try {
            await runConversation(command, {
              source: "wake-word",
              wakeWordDetected: true,
              latency: { wake_to_listen_ms: Math.round(performance.now() - lastWakeDetectedAtRef.current) },
            });
          } finally {
            scheduleWakeWordBusyRelease();
          }
          return;
        }

        if (activeComplete && !speakingNow && !normalized.includes(WAKE_WORD)) {
          const command = latest.trim();
          const now = performance.now();
          const duplicate = command === lastHandsFreeTranscriptRef.current
            && now - lastHandsFreeTranscriptAtRef.current < 1800;
          if (!command || duplicate) return;
          lastHandsFreeTranscriptRef.current = command;
          lastHandsFreeTranscriptAtRef.current = now;
          wakeWordBusyRef.current = true;
          setVoiceCommand(command);
          setWakeWordHeard(command);
          setLocalVoiceState(VOICE_STATES.SPEECH_DETECTED, {
            wake_word_mode: WAKE_WORD_MODES.BROWSER,
            partial_transcript: command,
            privacy: { mic: "MIC_ON", listening: true, notice: "Ich höre dich …" },
          });
          try {
            await runConversation(command, {
              source: "voice",
              wakeWordDetected: false,
              latency: { wake_to_listen_ms: 0 },
            });
          } finally {
            scheduleWakeWordBusyRelease();
          }
        }
      };

      recognition.onerror = async (event) => {
        const code = classifySpeechRecognitionError(event?.error);
        if (!shouldStopContinuousRecognitionAfterError(code)) return;

        const errorMessage = code === "MIC_PERMISSION_DENIED"
          ? normalizeVoiceError({ name: "NotAllowedError", message: "MIC_PERMISSION_DENIED" }, selectedPrimaryLanguage)
          : code === "MIC_CAPTURE_FAILED"
            ? normalizeVoiceError({ name: "NotReadableError" }, selectedPrimaryLanguage)
            : code === "NETWORK_ERROR"
              ? normalizeVoiceError({ message: "NETWORK_ERROR" }, selectedPrimaryLanguage)
              : code === "LANGUAGE_NOT_SUPPORTED"
                ? voiceClientErrorCopy(selectedPrimaryLanguage, "speech")
                : normalizeVoiceError({ message: "STT_FAILED" }, selectedPrimaryLanguage);

        setVoiceError(errorMessage);
        setLocalVoiceState(VOICE_STATES.ERROR, { error_code: code });
        await syncRuntimeState({
          voice_state: VOICE_STATES.ERROR,
          wake_word_mode: WAKE_WORD_MODES.BROWSER,
          error_code: code,
        }, { silent: true });
        await stopWakeWordMode({ preserveError: true });
      };

      recognition.onend = () => {
        wakeRecognitionSegments = [];
        if (!wakeWordEnabledRef.current) return;
        try {
          recognition.start();
        } catch {
          window.setTimeout(() => {
            if (wakeWordEnabledRef.current) {
              try {
                recognition.start();
              } catch {
                setVoiceError(voiceClientErrorCopy(selectedPrimaryLanguage, "retry"));
              }
            }
          }, 600);
        }
      };

      recognitionRef.current = recognition;
      recognition.start();
    } catch (error) {
      setVoiceError(normalizeVoiceError(error, selectedPrimaryLanguage));
      await stopWakeWordMode({ preserveError: true });
    }
  };

  const stopVoiceRecording = useCallback(({ disableHandsFree = true, reason = "manual_stop" } = {}) => {
    recordingStopReasonRef.current = reason;
    if (disableHandsFree) {
      setHandsFreeMicEnabled(false);
      handsFreeMicEnabledRef.current = false;
      clearHandsFreeRestart();
    }
    setVoiceRecording(false);
    setVoiceInputMode(handsFreeMicEnabledRef.current ? "recording" : "text");
    if (manualRecognitionActiveRef.current && recognitionRef.current && !wakeWordEnabledRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {
        // ignore native speech recognition stop failures
      }
      return;
    }
    if (!mediaRecorderRef.current || mediaRecorderRef.current.state === "inactive") return;
    mediaRecorderRef.current.stop();
  }, [clearHandsFreeRestart]);

  const handleAudioFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (hasActiveOutput()) {
      await stopCurrentSpeech({ notifyBackend: false, syncNextState: false, reason: "barge_in_upload" });
    }
    setVoiceError("");
    clearHandsFreeRestart();
    voicePendingRef.current = true;
    setVoicePending(true);
    setVoiceInputMode("upload");
    try {
      const audioBase64 = await blobToBase64(file);
      const type = file.type || "";
      const lowerName = String(file.name || "").toLowerCase();
      const audioFormat = lowerName.endsWith(".ogg")
        ? "ogg"
        : lowerName.endsWith(".m4a")
          ? "m4a"
          : lowerName.endsWith(".mp4")
            ? "mp4"
            : lowerName.endsWith(".mp3")
              ? "mp3"
              : lowerName.endsWith(".wav")
                ? "wav"
                : getVoiceAudioFormatForMime(type);
      await processAudioConversation(audioBase64, audioFormat, { source: "upload" });
    } catch (error) {
      setVoiceError(normalizeVoiceError(error, selectedPrimaryLanguage));
      setLocalVoiceState(VOICE_STATES.ERROR, { error_code: "STT_FAILED" });
    } finally {
      if (audioFileInputRef.current) audioFileInputRef.current.value = "";
      voicePendingRef.current = false;
      setVoicePending(false);
      setVoiceInputMode("text");
    }
  };

  const openAudioFilePicker = () => {
    audioFileInputRef.current?.click();
  };

  const startVoiceRecording = async ({ fromHandsFreeLoop = false, manualOneShot = false } = {}) => {
    setVoiceError("");
    if (policyBlocked) {
      setVoiceError(voiceClientErrorCopy(selectedPrimaryLanguage, "mic"));
      setLocalVoiceState(VOICE_STATES.MUTED, { privacy: { mic: "MIC_OFF", listening: false, notice: "Mikrofon ist aus." } });
      return;
    }
    if (voiceMutedRef.current) {
      setVoiceError(voiceClientErrorCopy(selectedPrimaryLanguage, "mic"));
      setLocalVoiceState(VOICE_STATES.MUTED, { privacy: { mic: "MIC_OFF", listening: false, notice: "Mikrofon ist aus." } });
      return;
    }
    clearHandsFreeRestart();
    const isIOSBrowser = isCurrentIOSLikeBrowser();
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const mediaRecorderFallbackAvailable = Boolean(navigator.mediaDevices?.getUserMedia) && typeof MediaRecorder !== "undefined";
    const captureStrategy = resolveVoiceCaptureStrategy({
      navigatorLike: navigator,
      hasSpeechRecognition: Boolean(SpeechRecognition),
      hasMediaDevices: Boolean(navigator.mediaDevices?.getUserMedia),
      hasMediaRecorder: typeof MediaRecorder !== "undefined",
    });
    const nativeLanguageBlocked = nativeRecognitionUnsupportedLanguagesRef.current.has(selectedProviderLanguage);
    const nativeRecognitionDisabled = isAutoLanguageMode || nativeLanguageBlocked;
    const canUseNativeIPhoneRecognition = captureStrategy === "ios-native" && !nativeRecognitionDisabled;
    if (
      captureStrategy === "unsupported"
      || (captureStrategy === "ios-native" && nativeRecognitionDisabled && !mediaRecorderFallbackAvailable)
    ) {
      setVoiceError(voiceClientErrorCopy(selectedPrimaryLanguage, "mic"));
      return;
    }
    if (
      voiceCaptureStartingRef.current
      || manualRecognitionActiveRef.current
      || (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive")
    ) {
      return;
    }
    voiceCaptureStartingRef.current = true;
    try {
      setVoiceInputMode("recording");
      await ensureOutputContextUnlocked({ prime: true }).catch(() => null);
      if (hasActiveOutput()) {
        voiceOperationIdRef.current += 1;
        activeRealtimeTurnIdRef.current = null;
        await stopCurrentSpeech({
          notifyBackend: false,
          syncNextState: false,
          reason: "barge_in_microphone",
          nextState: VOICE_STATES.LISTENING,
          nextWakeWordMode: WAKE_WORD_MODES.DISABLED,
        });
      }
      if (canUseNativeIPhoneRecognition) {
        let finalTranscript = "";
        let recognitionSegments = [];
        let recognitionFailed = false;
        let recognitionComplete = false;
        let recognitionStateSync = Promise.resolve();
        let gotSpeech = false;
        const recognition = new SpeechRecognition();
        recognitionRef.current = recognition;
        manualRecognitionActiveRef.current = true;
        activeMicTurnRef.current = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          submitted: false,
          manualOneShot,
        };
        recordingStopReasonRef.current = "manual_stop";
        recognition.lang = browserLocaleFor(selectedPrimaryLanguage);
        recognition.interimResults = true;
        recognition.continuous = false;
        recognition.maxAlternatives = 1;

        noAudioTimerRef.current = window.setTimeout(() => {
          if (!gotSpeech) {
            recordingStopReasonRef.current = "no_speech_timeout";
            try { recognition.stop(); } catch { /* native speech recognition may already be closed */ }
          }
        }, IOS_NO_AUDIO_TIMEOUT_MS);

        maxRecordingTimerRef.current = window.setTimeout(() => {
          recordingStopReasonRef.current = "max_recording_timeout";
          try { recognition.stop(); } catch { /* native speech recognition may already be closed */ }
        }, IOS_MAX_RECORDING_TIMEOUT_MS);

        recognition.onresult = async (event) => {
          const mergedResults = mergeSpeechRecognitionResults(recognitionSegments, event);
          recognitionSegments = mergedResults.segments;
          const latest = mergedResults.transcript;
          if (!latest) return;
          gotSpeech = true;
          finalTranscript = latest;
          recognitionComplete = mergedResults.isComplete;
          setAudioInputLevel(0.6);
          setLocalVoiceState(VOICE_STATES.SPEECH_DETECTED, {
            wake_word_mode: WAKE_WORD_MODES.DISABLED,
            partial_transcript: latest,
            privacy: { mic: "MIC_ON", listening: true, notice: "Ich höre dich …" },
          });
          recognitionStateSync = syncRuntimeState({
            voice_state: VOICE_STATES.SPEECH_DETECTED,
            wake_word_mode: WAKE_WORD_MODES.DISABLED,
            partial_transcript: latest,
            privacy: { mic: "MIC_ON", listening: true, notice: "Ich höre dich …" },
          }, { silent: true }).catch(() => undefined);
          await recognitionStateSync;
          if (mergedResults.isComplete) {
            recordingStopReasonRef.current = "speech_recognition_final";
            try { recognition.stop(); } catch { /* native speech recognition may already be closed */ }
          }
        };

        recognition.onerror = async (event) => {
          recognitionFailed = true;
          const code = classifySpeechRecognitionError(event?.error);
          const canFallbackFromNativeLanguage = code === "LANGUAGE_NOT_SUPPORTED" && mediaRecorderFallbackAvailable;
          if (canFallbackFromNativeLanguage) {
            nativeRecognitionUnsupportedLanguagesRef.current.add(selectedProviderLanguage);
          }
          const keepHandsFree = shouldPreserveHandsFree({
            reason: "recognition_error",
            handsFreeEnabled: handsFreeMicEnabledRef.current,
            manualOneShot,
          }) && (shouldPreserveHandsFreeAfterRecognitionError(code) || canFallbackFromNativeLanguage);
          if (!keepHandsFree) {
            setHandsFreeMicEnabled(false);
            handsFreeMicEnabledRef.current = false;
            clearHandsFreeRestart();
          }
          clearRecordingResources();
          setAudioInputLevel(0);
          setVoiceRecording(false);
          voicePendingRef.current = false;
          setVoicePending(false);
          manualRecognitionActiveRef.current = false;
          recognitionRef.current = null;
          activeMicTurnRef.current = null;
          if (!keepHandsFree) {
            resetManualMicMode();
          } else {
            setVoiceInputMode("recording");
          }
          const errorMessage = code === "NO_AUDIO"
            ? voiceClientErrorCopy(selectedPrimaryLanguage, "audio")
            : code === "MIC_PERMISSION_DENIED"
              ? normalizeVoiceError({ name: "NotAllowedError", message: "MIC_PERMISSION_DENIED" }, selectedPrimaryLanguage)
              : code === "MIC_CAPTURE_FAILED"
                ? normalizeVoiceError({ name: "NotReadableError" }, selectedPrimaryLanguage)
                : code === "NETWORK_ERROR"
                  ? normalizeVoiceError({ message: "NETWORK_ERROR" }, selectedPrimaryLanguage)
                  : code === "LANGUAGE_NOT_SUPPORTED"
                    ? voiceClientErrorCopy(selectedPrimaryLanguage, "speech")
                    : code === "RECOGNITION_ABORTED"
                      ? voiceClientErrorCopy(selectedPrimaryLanguage, "mic")
                      : normalizeVoiceError({ message: "STT_FAILED" }, selectedPrimaryLanguage);
          setVoiceError(errorMessage);
          setLocalVoiceState(VOICE_STATES.ERROR, { error_code: code });
          await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current, preserveError: true });
        };

        recognition.onend = async () => {
          await recognitionStateSync;
          const keepHandsFree = shouldPreserveHandsFree({
            reason: "recognition_end",
            handsFreeEnabled: handsFreeMicEnabledRef.current,
            manualOneShot,
          });
          clearRecordingResources();
          setAudioInputLevel(0);
          setVoiceRecording(false);
          voicePendingRef.current = false;
          setVoicePending(false);
          manualRecognitionActiveRef.current = false;
          recognitionRef.current = null;
          activeMicTurnRef.current = null;
          if (!keepHandsFree) {
            resetManualMicMode();
          } else {
            setVoiceInputMode("recording");
          }
          if (recognitionFailed) return;
          if (!finalTranscript.trim()) {
            setVoiceError(voiceClientErrorCopy(selectedPrimaryLanguage, "audio"));
            setLocalVoiceState(VOICE_STATES.ERROR, { error_code: "NO_AUDIO" });
            await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current, preserveError: true });
            return;
          }
          if (!recognitionComplete) {
            const privacy = { mic: "MIC_OFF", listening: false, notice: "Die Sprache wurde nicht vollständig erkannt." };
            setVoiceError(voiceClientErrorCopy(selectedPrimaryLanguage, "audio"));
            setLocalVoiceState(VOICE_STATES.ERROR, {
              wake_word_mode: WAKE_WORD_MODES.DISABLED,
              error_code: "INCOMPLETE_TRANSCRIPT",
              partial_transcript: finalTranscript,
              privacy,
            });
            await syncRuntimeState({
              voice_state: VOICE_STATES.ERROR,
              wake_word_mode: WAKE_WORD_MODES.DISABLED,
              error_code: "INCOMPLETE_TRANSCRIPT",
              partial_transcript: finalTranscript,
              privacy,
            }, { silent: true });
            if (keepHandsFree) {
              await transitionToReadyStateRef.current?.({ sync: true, followUp: false, preserveError: true });
            }
            return;
          }
          setLocalVoiceState(VOICE_STATES.TRANSCRIBING, {
            wake_word_mode: WAKE_WORD_MODES.DISABLED,
            partial_transcript: finalTranscript,
            privacy: { mic: "MIC_OFF", listening: false, notice: "Ich verstehe …" },
          });
          await syncRuntimeState({
            voice_state: VOICE_STATES.TRANSCRIBING,
            wake_word_mode: WAKE_WORD_MODES.DISABLED,
            partial_transcript: finalTranscript,
            privacy: { mic: "MIC_OFF", listening: false, notice: "Ich verstehe …" },
          }, { silent: true });
          try {
            await processRecognizedTranscript(finalTranscript, { source: "audio", detectedLanguage: selectedPrimaryLanguage });
          } catch {
            // downstream handler already surfaced the error state
          }
        };

        setVoiceRecording(true);
        if (!fromHandsFreeLoop && !manualOneShot) {
          setHandsFreeMicEnabled(true);
          handsFreeMicEnabledRef.current = true;
        } else if (manualOneShot) {
          setHandsFreeMicEnabled(false);
          handsFreeMicEnabledRef.current = false;
        }
        clearHandsFreeRestart();
        setLocalVoiceState(VOICE_STATES.LISTENING, {
          wake_word_mode: WAKE_WORD_MODES.DISABLED,
          privacy: { mic: "MIC_ON", listening: true, notice: "Ich höre zu …" },
        });
        recognition.start();
        void syncRuntimeState({
          voice_state: VOICE_STATES.LISTENING,
          wake_word_mode: WAKE_WORD_MODES.DISABLED,
          privacy: { mic: "MIC_ON", listening: true, notice: "Ich höre zu …" },
        }, { silent: true });
        voiceCaptureStartingRef.current = false;
        return;
      }
      const existingStream = mediaStreamRef.current;
      const reusableStream = Boolean(
        existingStream
        && existingStream.getAudioTracks().some((track) => track.readyState === "live" && track.enabled),
      );
      if (existingStream && !reusableStream) {
        existingStream.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
      let stream = existingStream;
      if (!reusableStream) {
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
              channelCount: 1,
            },
          });
        } catch (captureError) {
          if (!shouldRetryBasicAudioCapture(captureError)) throw captureError;
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        }
      }
      mediaStreamRef.current = stream;
      const mimeCandidates = getVoiceRecorderMimeCandidates(isIOSBrowser);
      const supportedMime = mimeCandidates.find((item) => typeof MediaRecorder.isTypeSupported === "function" && MediaRecorder.isTypeSupported(item)) || "";
      const recorderOptions = { ...(supportedMime ? { mimeType: supportedMime } : {}), audioBitsPerSecond: isIOSBrowser ? 64000 : 96000 };
      let recorder;
      try {
        recorder = new MediaRecorder(stream, recorderOptions);
      } catch {
        recorder = supportedMime ? new MediaRecorder(stream, { mimeType: supportedMime }) : new MediaRecorder(stream);
      }
      recordedChunksRef.current = [];
      mediaRecorderRef.current = recorder;
      activeMicTurnRef.current = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        submitted: false,
        manualOneShot,
      };
      recordingStopReasonRef.current = "manual_stop";

      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      if (!AudioContextCtor) {
        setVadSupported(false);
      } else {
        let vadAudioContext = null;
        try {
          vadAudioContext = new AudioContextCtor();
          await resumeAudioContextIfNeeded(vadAudioContext);
          if (vadAudioContext.state && vadAudioContext.state !== "running") {
            throw new Error("VAD_AUDIO_CONTEXT_NOT_RUNNING");
          }
          const sourceNode = vadAudioContext.createMediaStreamSource(stream);
          const analyser = vadAudioContext.createAnalyser();
          analyser.fftSize = 2048;
          sourceNode.connect(analyser);
          audioContextRef.current = vadAudioContext;
          analyserRef.current = analyser;
          setVadSupported(true);
        } catch {
          setVadSupported(false);
          analyserRef.current = null;
          if (vadAudioContext) {
            try {
              await vadAudioContext.close();
            } catch {
              // VAD is optional; recording can continue without an analyser.
            }
          }
        }
      }

      const vadState = { hasSpeech: false, lastSpeechAt: 0, syncedSpeechDetected: false };
      const silenceStopMs = isIOSBrowser ? IOS_SILENCE_STOP_MS : SILENCE_STOP_MS;
      const noAudioTimeoutMs = isIOSBrowser ? IOS_NO_AUDIO_TIMEOUT_MS : NO_AUDIO_TIMEOUT_MS;
      const maxRecordingTimeoutMs = isIOSBrowser ? IOS_MAX_RECORDING_TIMEOUT_MS : MAX_RECORDING_TIMEOUT_MS;
      const monitorVoiceActivity = () => {
        if (!analyserRef.current || !mediaRecorderRef.current || mediaRecorderRef.current.state === "inactive") return;
        const data = new Uint8Array(analyserRef.current.fftSize);
        analyserRef.current.getByteTimeDomainData(data);
        let sum = 0;
        for (let index = 0; index < data.length; index += 1) {
          const sample = (data[index] - 128) / 128;
          sum += sample * sample;
        }
        const rms = Math.sqrt(sum / data.length);
        setAudioInputLevel(normalizeAudioLevel(rms));
        if (rms > RMS_SPEECH_THRESHOLD) {
          vadState.hasSpeech = true;
          vadState.lastSpeechAt = Date.now();
          setLocalVoiceState(VOICE_STATES.SPEECH_DETECTED, {
            wake_word_mode: WAKE_WORD_MODES.DISABLED,
            privacy: { mic: "MIC_ON", listening: true, notice: "Ich höre dich …" },
          });
          if (!vadState.syncedSpeechDetected) {
            vadState.syncedSpeechDetected = true;
            void syncRuntimeState({
              voice_state: VOICE_STATES.SPEECH_DETECTED,
              wake_word_mode: WAKE_WORD_MODES.DISABLED,
              privacy: { mic: "MIC_ON", listening: true, notice: "Ich höre dich …" },
            }, { silent: true });
          }
        }
        if (vadState.hasSpeech && Date.now() - vadState.lastSpeechAt > silenceStopMs) {
          stopVoiceRecording({
            disableHandsFree: shouldDisableHandsFreeForStop({
              reason: "silence_detected",
              handsFreeEnabled: handsFreeMicEnabledRef.current,
              manualOneShot,
            }),
            reason: "silence_detected",
          });
          return;
        }
        vadFrameRef.current = window.requestAnimationFrame(monitorVoiceActivity);
      };

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) recordedChunksRef.current.push(event.data);
      };

      recorder.onerror = async () => {
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.onerror = null;
        recordedChunksRef.current = [];
        activeMicTurnRef.current = null;
        voiceCaptureStartingRef.current = false;
        voicePendingRef.current = false;
        setVoicePending(false);
        setVoiceRecording(false);
        setHandsFreeMicEnabled(false);
        handsFreeMicEnabledRef.current = false;
        clearHandsFreeRestart();
        try {
          if (recorder.state !== "inactive") recorder.stop();
        } catch {
          // ignore recorder shutdown races after a fatal recorder error
        }
        clearRecordingResources();
        resetManualMicMode();
        setVoiceError(voiceClientErrorCopy(selectedPrimaryLanguage, "mic"));
        setLocalVoiceState(VOICE_STATES.ERROR, {
          wake_word_mode: WAKE_WORD_MODES.DISABLED,
          error_code: "RECORDER_ERROR",
          privacy: { mic: "MIC_OFF", listening: false, notice: "Mikrofonaufnahme wurde unterbrochen." },
        });
        await syncRuntimeState({
          voice_state: VOICE_STATES.ERROR,
          wake_word_mode: WAKE_WORD_MODES.DISABLED,
          error_code: "RECORDER_ERROR",
          privacy: { mic: "MIC_OFF", listening: false, notice: "Mikrofonaufnahme wurde unterbrochen." },
        }, { silent: true });
      };

      recorder.onstop = async () => {
        let shouldAutoResumeInFinally = false;
        try {
          if (activeMicTurnRef.current?.submitted) {
            return;
          }
          if (activeMicTurnRef.current) {
            activeMicTurnRef.current.submitted = true;
          }
          const finalMime = recorder.mimeType || supportedMime || "audio/webm";
          const audioFormat = getVoiceAudioFormatForMime(finalMime);
          const blob = new Blob(recordedChunksRef.current, { type: finalMime });
          if (!blob.size) {
            const noSpeech = recordingStopReasonRef.current === "no_speech_timeout" || !vadState.hasSpeech;
            setVoiceError(voiceClientErrorCopy(selectedPrimaryLanguage, "audio"));
            setLocalVoiceState(VOICE_STATES.ERROR, { error_code: "NO_AUDIO" });
            await syncRuntimeState({ voice_state: VOICE_STATES.ERROR, error_code: "NO_AUDIO", wake_word_mode: WAKE_WORD_MODES.DISABLED }, { silent: true });
            shouldAutoResumeInFinally = true;
            return;
          }
          const audioBase64 = await blobToBase64(blob);
          const result = await processAudioConversation(audioBase64, audioFormat, { source: "audio" });
          shouldAutoResumeInFinally = !result;
        } catch (error) {
          setVoiceError(normalizeVoiceError(error, selectedPrimaryLanguage));
          setLocalVoiceState(VOICE_STATES.ERROR, { error_code: "STT_FAILED" });
          shouldAutoResumeInFinally = true;
        } finally {
          clearRecordingResources({ keepStream: handsFreeMicEnabledRef.current });
          activeMicTurnRef.current = null;
          voicePendingRef.current = false;
          setVoicePending(false);
          setVoiceRecording(false);
          if (handsFreeMicEnabledRef.current && !voiceMuted && !policyBlocked) {
            setVoiceInputMode("recording");
            if (shouldAutoResumeInFinally && !hasActiveOutput()) {
              clearHandsFreeRestart();
              handsFreeRestartTimerRef.current = window.setTimeout(() => {
                handsFreeRestartTimerRef.current = null;
                if (!handsFreeMicEnabledRef.current || voiceMuted || policyBlocked || wakeWordEnabledRef.current || hasActiveOutput() || voicePendingRef.current) return;
                void startVoiceRecordingRef.current?.({ fromHandsFreeLoop: true });
              }, 420);
            }
          } else {
            resetManualMicMode();
            if (shouldAutoResumeInFinally || (recordingStopReasonRef.current === "max_recording_timeout" && !vadState.hasSpeech)) {
              await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current, preserveError: true });
            }
          }
          recordingStopReasonRef.current = "manual_stop";
        }
      };

      if (analyserRef.current) {
        noAudioTimerRef.current = window.setTimeout(() => {
          if (!vadState.hasSpeech) {
            stopVoiceRecording({
              disableHandsFree: shouldDisableHandsFreeForStop({
                reason: "no_speech_timeout",
                handsFreeEnabled: handsFreeMicEnabledRef.current,
                manualOneShot,
              }),
              reason: "no_speech_timeout",
            });
          }
        }, noAudioTimeoutMs);
      } else {
        noAudioTimerRef.current = null;
      }

      maxRecordingTimerRef.current = window.setTimeout(() => {
        stopVoiceRecording({
            disableHandsFree: shouldDisableHandsFreeForStop({
              reason: "max_recording_timeout",
              handsFreeEnabled: handsFreeMicEnabledRef.current,
              manualOneShot,
            }),
            reason: "max_recording_timeout",
          });
      }, maxRecordingTimeoutMs);

      recorder.start(250);
      setVoiceRecording(true);
      if (!fromHandsFreeLoop && !manualOneShot) {
        setHandsFreeMicEnabled(true);
        handsFreeMicEnabledRef.current = true;
      } else if (manualOneShot) {
        setHandsFreeMicEnabled(false);
        handsFreeMicEnabledRef.current = false;
        clearHandsFreeRestart();
      }
      setLocalVoiceState(VOICE_STATES.LISTENING, {
        wake_word_mode: WAKE_WORD_MODES.DISABLED,
        privacy: { mic: "MIC_ON", listening: true, notice: manualOneShot ? "Ich höre zu …" : "Ich höre dauerhaft zu …" },
      });
      await syncRuntimeState({
        voice_state: VOICE_STATES.LISTENING,
        wake_word_mode: WAKE_WORD_MODES.DISABLED,
        privacy: { mic: "MIC_ON", listening: true, notice: manualOneShot ? "Ich höre zu …" : "Ich höre dauerhaft zu …" },
      }, { silent: true });
      monitorVoiceActivity();
      voiceCaptureStartingRef.current = false;
    } catch (error) {
      voiceCaptureStartingRef.current = false;
      if (manualRecognitionActiveRef.current && recognitionRef.current && !wakeWordEnabledRef.current) {
        try {
          recognitionRef.current.onresult = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.onend = null;
          recognitionRef.current.stop();
        } catch {
          // ignore native recognition startup/stop races
        }
        recognitionRef.current = null;
      }
      manualRecognitionActiveRef.current = false;
      activeMicTurnRef.current = null;
      setVoiceRecording(false);
      clearRecordingResources();
      resetManualMicMode();
      setVoiceError(normalizeVoiceError(error, selectedPrimaryLanguage));
      setLocalVoiceState(VOICE_STATES.ERROR, { error_code: voiceCaptureErrorCode(error) });
      await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current, preserveError: true });
    }
  };

  startVoiceRecordingRef.current = startVoiceRecording;

  useEffect(() => {
    if (!handsFreeMicEnabled || voiceMuted || policyBlocked || wakeWordArmed || voiceRecording || voicePending || hasActiveOutput()) {
      return undefined;
    }
    const state = normalizeVoiceState(voiceStatus.voice_state);
    if ([VOICE_STATES.TRANSCRIBING, VOICE_STATES.THINKING, VOICE_STATES.RESPONDING, VOICE_STATES.EXECUTING, VOICE_STATES.SPEAKING].includes(state)) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      if (!handsFreeMicEnabledRef.current || voiceMuted || policyBlocked || wakeWordEnabledRef.current || voicePendingRef.current || hasActiveOutput()) return;
      void startVoiceRecordingRef.current?.({ fromHandsFreeLoop: true });
    }, 700);
    return () => window.clearTimeout(timer);
  }, [handsFreeMicEnabled, hasActiveOutput, policyBlocked, voiceMuted, voicePending, voiceRecording, voiceStatus.voice_state, wakeWordArmed]);

  const toggleAlwaysListening = useCallback(async () => {
    if (voiceRecording || handsFreeMicEnabledRef.current) {
      stopVoiceRecording({ disableHandsFree: true, reason: "manual_stop" });
      return;
    }
    if (wakeWordEnabledRef.current) {
      await stopWakeWordMode();
    }
    resetManualMicMode();
    await startVoiceRecordingRef.current?.({ fromHandsFreeLoop: false, manualOneShot: false });
  }, [resetManualMicMode, stopVoiceRecording, stopWakeWordMode, voiceRecording]);

  const stopVoiceActivity = useCallback(async () => {
    const currentState = normalizeVoiceState(voiceStatus.voice_state);
    if (mediaRecorderRef.current?.state === "recording" || voiceRecording || manualRecognitionActiveRef.current) {
      voiceOperationIdRef.current += 1;
      activeRealtimeTurnIdRef.current = null;
      voicePendingRef.current = false;
      setVoicePending(false);
      setHandsFreeMicEnabled(false);
      handsFreeMicEnabledRef.current = false;
      clearHandsFreeRestart();

      if (manualRecognitionActiveRef.current && recognitionRef.current && !wakeWordEnabledRef.current) {
        try {
          recognitionRef.current.onresult = null;
          recognitionRef.current.onerror = null;
          recognitionRef.current.onend = null;
          recognitionRef.current.stop();
        } catch {
          // ignore native recognition cancellation races
        }
        recognitionRef.current = null;
      }
      manualRecognitionActiveRef.current = false;
      activeMicTurnRef.current = null;

      if (mediaRecorderRef.current) {
        const recorder = mediaRecorderRef.current;
        recorder.ondataavailable = null;
        recorder.onstop = null;
        recorder.onerror = null;
        if (recorder.state !== "inactive") {
          try {
            recorder.stop();
          } catch {
            // ignore recorder cancellation races
          }
        }
      }

      clearRecordingResources();
      setVoiceRecording(false);
      resetManualMicMode();
      await transitionToReadyStateRef.current?.({ sync: true, followUp: false });
      return;
    }
    if ([VOICE_STATES.TRANSCRIBING, VOICE_STATES.THINKING, VOICE_STATES.RESPONDING, VOICE_STATES.EXECUTING].includes(currentState)) {
      voiceOperationIdRef.current += 1;
      activeRealtimeTurnIdRef.current = null;
      voicePendingRef.current = false;
      setVoicePending(false);
      await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current });
      return;
    }
    if ([VOICE_STATES.SPEAKING, VOICE_STATES.LISTENING, VOICE_STATES.SPEECH_DETECTED, VOICE_STATES.WAKE].includes(currentState) || hasActiveOutput()) {
      voiceOperationIdRef.current += 1;
      activeRealtimeTurnIdRef.current = null;
      voicePendingRef.current = false;
      setVoicePending(false);
      await stopCurrentSpeech({
        notifyBackend: true,
        reason: "manual_stop_button",
        nextState: wakeWordArmed ? VOICE_STATES.WAKE : (voiceMuted ? VOICE_STATES.MUTED : VOICE_STATES.IDLE),
        nextWakeWordMode: wakeWordArmed ? WAKE_WORD_MODES.BROWSER : WAKE_WORD_MODES.DISABLED,
      });
      return;
    }
    await transitionToReadyStateRef.current?.({ sync: true, followUp: wakeWordEnabledRef.current });
  }, [clearHandsFreeRestart, clearRecordingResources, hasActiveOutput, resetManualMicMode, setVoicePending, stopCurrentSpeech, voiceMuted, voiceRecording, voiceStatus.voice_state, wakeWordArmed]);

  const toggleVoiceMute = useCallback(async () => {
    if (policyBlocked) {
      setVoiceMuted(true);
      setVoiceError(voiceClientErrorCopy(selectedPrimaryLanguage, "mic"));
      return;
    }
    if (voiceMuted) {
      voiceMutedRef.current = false;
      setVoiceMuted(false);
      setVoiceError("");
      await transitionToReadyStateRef.current?.({ sync: true, followUp: false });
      return;
    }

    voiceOperationIdRef.current += 1;
    activeRealtimeTurnIdRef.current = null;
    voicePendingRef.current = false;
    setVoicePending(false);
    voiceMutedRef.current = true;
    setVoiceMuted(true);
    setHandsFreeMicEnabled(false);
    handsFreeMicEnabledRef.current = false;
    clearHandsFreeRestart();
    setVoiceError("");
    if (mediaRecorderRef.current) {
      const recorder = mediaRecorderRef.current;
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
      if (recorder.state !== "inactive") {
        try {
          recorder.stop();
        } catch {
          // ignore recorder cancellation races while muting
        }
      }
    }
    recordedChunksRef.current = [];
    activeMicTurnRef.current = null;
    voiceCaptureStartingRef.current = false;
    setVoiceRecording(false);
    clearRecordingResources();
    await stopWakeWordMode({ toMuted: true });
    if (hasActiveOutput()) {
      await stopCurrentSpeech({ notifyBackend: true, reason: "manual_mute", nextState: VOICE_STATES.MUTED, nextWakeWordMode: WAKE_WORD_MODES.DISABLED, nextPrivacy: { mic: "MIC_OFF", listening: false, notice: "Mikrofon ist aus." } });
      return;
    }
    setLocalVoiceState(VOICE_STATES.MUTED, {
      wake_word_mode: WAKE_WORD_MODES.DISABLED,
      privacy: { mic: "MIC_OFF", listening: false, notice: "Mikrofon ist aus." },
    });
    await syncRuntimeState({
      voice_state: VOICE_STATES.MUTED,
      wake_word_mode: WAKE_WORD_MODES.DISABLED,
      privacy: { mic: "MIC_OFF", listening: false, notice: "Mikrofon ist aus." },
    }, { silent: true });
  }, [clearHandsFreeRestart, clearRecordingResources, hasActiveOutput, policyBlocked, selectedPrimaryLanguage, stopCurrentSpeech, stopWakeWordMode, syncRuntimeState, voiceMuted, setLocalVoiceState]);

  const handleVoiceLanguageToggle = async (languageCode) => {
    const normalized = languageCodeForProvider(languageCode);
    if (!normalized) return;
    const saveGeneration = voiceLanguageSaveGenerationRef.current + 1;
    voiceLanguageSaveGenerationRef.current = saveGeneration;
    const previousLanguages = selectedVoiceLanguages;
    setSelectedVoiceLanguages([normalized]);
    setVoiceError("");
    try {
      const updatedMemory = await queueVoicePreferenceSave(() => updateMemoryPreferences({
        preferred_languages: [normalized],
        preferred_language: normalized,
        assistant_voice_profile: assistantVoiceProfile,
      }, token));
      if (saveGeneration !== voiceLanguageSaveGenerationRef.current) return;
      const lockedLanguage = languageCodeForProvider(updatedMemory?.preferred_language || normalized) || normalized;
      setSelectedVoiceLanguages([lockedLanguage]);
      setVoiceDisplay((prev) => ({ ...prev, language: lockedLanguage }));
      try {
        await onRefresh?.();
      } catch {
        // Preference persistence already succeeded; refresh is best-effort only.
      }
      if (saveGeneration !== voiceLanguageSaveGenerationRef.current) return;
      toast.success("Sprache gespeichert", {
        description: `AION hört und antwortet jetzt fest in ${singleLanguageLabel(lockedLanguage)}.`,
      });
    } catch (error) {
      if (saveGeneration !== voiceLanguageSaveGenerationRef.current) return;
      setSelectedVoiceLanguages(previousLanguages.length ? previousLanguages : [selectedPrimaryLanguage || "de"]);
      setVoiceError(normalizeVoiceError(error, normalized));
    }
  };

  const handleVoiceAutoLanguagesSave = async () => {
    const saveGeneration = voiceLanguageSaveGenerationRef.current + 1;
    voiceLanguageSaveGenerationRef.current = saveGeneration;
    const previousLanguages = selectedVoiceLanguages;
    const allLanguages = Object.keys(VOICE_LANGUAGE_LABELS);
    const preferredLanguage = languageCodeForProvider(selectedPrimaryLanguage || memory?.preferred_language || "de") || "de";
    setSelectedVoiceLanguages(allLanguages);
    setVoiceError("");
    try {
      const updatedMemory = await queueVoicePreferenceSave(() => updateMemoryPreferences({
        preferred_languages: allLanguages,
        preferred_language: preferredLanguage,
        assistant_voice_profile: assistantVoiceProfile,
      }, token));
      if (saveGeneration !== voiceLanguageSaveGenerationRef.current) return;
      const confirmedLanguages = normalizeSelectedLanguages(updatedMemory?.preferred_languages || allLanguages).slice(0, 38);
      if (confirmedLanguages.length < 2) {
        throw new Error("Auto language mode requires the expanded language catalog");
      }
      const confirmedPrimary = languageCodeForProvider(updatedMemory?.preferred_language || preferredLanguage) || preferredLanguage;
      setSelectedVoiceLanguages(confirmedLanguages);
      setVoiceDisplay((prev) => ({ ...prev, language: confirmedPrimary }));
      try {
        await onRefresh?.();
      } catch {
        // Preference persistence already succeeded; refresh is best-effort only.
      }
      if (saveGeneration !== voiceLanguageSaveGenerationRef.current) return;
      toast.success("Auto-Sprachen aktiviert", {
        description: "AION erkennt und antwortet automatisch in " + confirmedLanguages.length + " freigegebenen Sprachen.",
      });
    } catch (error) {
      if (saveGeneration !== voiceLanguageSaveGenerationRef.current) return;
      setSelectedVoiceLanguages(previousLanguages.length ? previousLanguages : [preferredLanguage]);
      setVoiceError(normalizeVoiceError(error, preferredLanguage));
    }
  };

  const handleVoiceLanguagesSave = async () => {
    const saveGeneration = voiceLanguageSaveGenerationRef.current + 1;
    voiceLanguageSaveGenerationRef.current = saveGeneration;
    const preferredLanguage = languageCodeForProvider(selectedVoiceLanguages[0] || memory?.preferred_language || "de") || "de";
    const nextLanguages = [preferredLanguage];
    try {
      const updatedMemory = await queueVoicePreferenceSave(() => updateMemoryPreferences({
        preferred_languages: nextLanguages,
        preferred_language: preferredLanguage,
        assistant_voice_profile: assistantVoiceProfile,
      }, token));
      if (saveGeneration !== voiceLanguageSaveGenerationRef.current) return;
      const confirmedLanguages = normalizeSelectedLanguages(updatedMemory?.preferred_languages || nextLanguages).slice(0, 38);
      const lockedLanguage = updatedMemory?.preferred_language || confirmedLanguages[0] || preferredLanguage;
      setSelectedVoiceLanguages(confirmedLanguages.length ? confirmedLanguages : [lockedLanguage]);
      try {
        await onRefresh?.();
      } catch {
        // Preference persistence already succeeded; refresh is best-effort only.
      }
      if (saveGeneration !== voiceLanguageSaveGenerationRef.current) return;
      setVoiceDisplay((prev) => ({ ...prev, language: lockedLanguage }));
      setVoiceError("");
      toast.success("Sprachen gespeichert", {
        description: confirmedLanguages.length > 1
          ? `AION erkennt und antwortet automatisch in ${confirmedLanguages.length} ausgewählten Sprachen.`
          : `AION hört und antwortet jetzt fest in ${singleLanguageLabel(lockedLanguage)}.`,
      });
    } catch (error) {
      if (saveGeneration !== voiceLanguageSaveGenerationRef.current) return;
      setVoiceError(normalizeVoiceError(error, preferredLanguage));
    }
  };

  const handleAssistantVoiceProfileSave = async (profile) => {
    const normalizedProfile = profile === "male" ? "male" : "female";
    const saveGeneration = assistantVoiceProfileSaveGenerationRef.current + 1;
    assistantVoiceProfileSaveGenerationRef.current = saveGeneration;
    const previousProfile = assistantVoiceProfile;
    setAssistantVoiceProfile(normalizedProfile);
    try {
      const updatedMemory = await queueVoicePreferenceSave(() => updateMemoryPreferences({
        preferred_languages: normalizeSelectedLanguages(selectedVoiceLanguages),
        preferred_language: selectedPrimaryLanguage,
        assistant_voice_profile: normalizedProfile,
      }, token));
      if (saveGeneration !== assistantVoiceProfileSaveGenerationRef.current) return;
      setAssistantVoiceProfile(updatedMemory?.assistant_voice_profile === "male" ? "male" : "female");
      try {
        await onRefresh?.();
      } catch {
        // Voice profile persistence already succeeded; refresh is best-effort only.
      }
      if (saveGeneration !== assistantVoiceProfileSaveGenerationRef.current) return;
      toast.success("AION Stimme gespeichert", {
        description: normalizedProfile === "male" ? "Männliche Stimme und männlicher Avatar sind aktiv." : "Weibliche Stimme und weiblicher Avatar sind aktiv.",
      });
    } catch (error) {
      if (saveGeneration !== assistantVoiceProfileSaveGenerationRef.current) return;
      setAssistantVoiceProfile(previousProfile);
      setVoiceError(normalizeVoiceError(error, selectedPrimaryLanguage));
    }
  };

  useEffect(() => () => {
    if (mediaRecorderRef.current) {
      const recorder = mediaRecorderRef.current;
      recorder.ondataavailable = null;
      recorder.onstop = null;
      recorder.onerror = null;
      if (recorder.state !== "inactive") {
        try {
          recorder.stop();
        } catch {
          // ignore recorder shutdown races during navigation
        }
      }
    }
    wakeWordEnabledRef.current = false;
    clearWakeWordBusy();
    manualRecognitionActiveRef.current = false;
    activeMicTurnRef.current = null;
    voiceCaptureStartingRef.current = false;
    voiceOperationIdRef.current += 1;
    activeRealtimeTurnIdRef.current = null;
    voicePendingRef.current = false;
    if (recognitionRef.current) {
      try {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
      } catch {
        // ignore browser recognition shutdown races
      }
      recognitionRef.current = null;
    }
    clearRecordingResources();
    clearOutputAnalysis();
    clearWakeWindow();
    clearSpeechQueue();
    clearHandsFreeRestart();
    handsFreeMicEnabledRef.current = false;
    if (outputAudioContextRef.current) {
      outputAudioContextRef.current.close().catch(() => undefined);
      outputAudioContextRef.current = null;
    }
    clearOutputMediaElementSource();
    if (currentAudioRef.current) {
      const audio = currentAudioRef.current;
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
      audio.currentTime = 0;
      if (String(audio.src || "").startsWith("blob:")) {
        URL.revokeObjectURL(audio.src);
      }
      audio.removeAttribute?.("src");
      audio.load?.();
      currentAudioRef.current = null;
    }
  }, [clearHandsFreeRestart, clearOutputAnalysis, clearOutputMediaElementSource, clearRecordingResources, clearSpeechQueue, clearWakeWordBusy, clearWakeWindow]);

  return {
    assistantVoiceProfile,
    audioFileInputRef,
    audioInputLevel,
    audioOutputLevel,
    handleAssistantVoiceProfileSave,
    handleAudioFileSelected,
    handleVoiceAutoLanguagesSave,
    handleVoiceLanguageToggle,
    handleVoiceLanguagesSave,
    handleVoiceSubmit,
    interruptVoiceOutput: stopCurrentSpeech,
    memoryRecallResult,
    openAudioFilePicker,
    runConversation,
    selectedVoiceLanguages,
    setVoiceCommand,
    handleRealtimeVoiceEvent,
    handsFreeMicEnabled,
    startVoiceRecording,
    startWakeWordMode,
    stopSpeechOutput: stopVoiceActivity,
    stopVoiceRecording,
    stopWakeWordMode,
    toggleAlwaysListening,
    toggleVoiceMute,
    vadSupported,
    voiceCommand,
    voiceCaptureStrategy,
    voiceCaptureSupported,
    voiceDiagnostics: {
      ...voiceStatus,
      wake_word_label: getWakeWordModeLabel(voiceStatus.wake_word_mode),
      voice_muted: voiceMuted,
      vad_supported: vadSupported,
      browser_family: typeof window !== "undefined" ? getVoiceBrowserFamily(window.navigator || {}) : "unknown",
      capture_strategy: voiceCaptureStrategy,
    },
    voiceDisplay,
    voiceLanguageMode,
    voiceLanguageModeLabel,
    voiceDisplayState: voiceStatus.voice_ready && voiceStatus.stt_ready && voiceStatus.tts_ready
      ? (voiceStatus.voice_state === VOICE_STATES.IDLE ? "Voice konfiguriert" : voiceStatus.display_state)
      : "Voice nicht verbunden",
    voiceError,
    voiceInputMode,
    voiceMuted,
    voicePartialTranscript: voiceStatus.partial_transcript,
    voicePending,
    voiceRecording,
    voiceResult,
    voiceSessionId,
    voiceState: voiceStatus.voice_state,
    voiceStatus,
    wakeWordArmed,
    wakeWordHeard,
    wakeWordSupported,
  };
};
